/*
 * paex-quick-add.js — controller for the theme-owned Quick Add modal
 * (snippets/paex-quick-add.liquid; design: PLP All States groups E/F).
 *
 * Interception: the RCC app opens its own .rcc-atb-modal from the card's
 * button.rcc-search__product__quick-add-trigger. We listen on document in
 * the CAPTURE phase (registered at parse time, before the app's scripts
 * hydrate), resolve the product from the card's /products/ anchor, and
 * preventDefault + stopImmediatePropagation so the app handler never runs
 * — the app modal cannot open, and because the add goes through OUR
 * /cart/add.js call the app's retail-connect:add-to-cart:success bridge
 * in nimstrata.liquid never fires for these adds (no double handling).
 * If the product URL cannot be resolved the click is left untouched so
 * the app's modal remains a working fallback. Single-variant cards render
 * a different control (the add-to-basket form/BIS pill), never this
 * trigger, so direct adds are not intercepted (E-06).
 *
 * XSS: every piece of fetched product data lands via textContent or
 * attribute setters — no innerHTML anywhere in this file.
 *
 * SIZE CHART ROW (native size guide Phase 1, 2026-10-02 — replaces the MP
 * Size Chart app island): the chart comes from the Section Rendering API
 * (/products/{handle}?section_id=paex-size-guide-data → the same JSON the
 * PDP block emits, between [[PAEXSG]] sentinels, sliced from the raw text
 * and JSON.parsed — never DOM-parsed) and is rendered by the shared
 * renderer in assets/paex-size-guide.js (window.paexSizeGuide.render.table,
 * createElement/textContent only). The former AVADA/$scAppData path lives in
 * paex-docs/cleanup/_quarantine/assets/paex-quick-add-avada-size-chart.js (law 0.3).
 *
 * SIZE GUIDE PHASE 2 (2026-10-03, BUILD-PLAN §2 WP4 / QUICK-ADD-HOOKS §3-§8): badge +
 * GREEN pre-selection on the size pills, the shared guidance slot (render.row), the fit
 * line, the chart link opening OUR drawer stacked over the quick-add (render.drawer;
 * inline table = degrade path) and the readable "_Size advice" line property (2026-10-05,
 * ONE encoder: assets/paex-size-advice.js, fed by the base asset's outcomeParts). finder.resolve() is the single engine; copy = island strings only;
 * switch off = zero traces; logic/row/drawer assets load on intent or for profile holders.
 *
 * BOPIS (2026-10-22, lane E, Track 3 step 8 — plan 6b/6c, seams §3): the compact "Buy online"
 * selector (fieldset [data-paex-qa-services] in snippets/paex-quick-add.liquid) between the size
 * rows and the recs, per-size availability for the selected service (pill --fulfil-out), the
 * CTA label per service, the store quantity cap, the add through the bridge gate with
 * { handle, stock, mode } so paexFulfil.check() can read the ?view=paex-stock JSON, the recs
 * filtered by service and re-checked on paex:fulfil-change. Everything feature-detects
 * window.paexFulfil (assets/paex-fulfil.js) and its paex.fulfil.* strings (paexFulfil.t);
 * absent, or for a visitor outside a participating store's pickup radius with no cart mode,
 * the modal is today's byte for byte (the fieldset stays hidden, addToCart passes the same
 * payload). No string is literal here: labels come from paexFulfil.t / rccStrings.
 */
(() => {
  'use strict';

  const strings = () => window.rccStrings || {};
  const isFr = () => document.documentElement.lang.toLowerCase().startsWith('fr');
  const t = (key, fallbackEn, fallbackFr) => {
    const s = strings();
    return (typeof s[key] === 'string' && s[key]) || (isFr() ? fallbackFr : fallbackEn);
  };

  const state = {
    product: null,
    productPath: '',
    selections: [],
    opener: null,
    busy: false,
    requestToken: 0,
    // In-modal navigation stack for recommendation swaps (design E-05/F-04):
    // entries {path, title}; back pops one level; capped at 5 (the design's
    // anti-pattern guard — beyond that the top entry is replaced).
    stack: [],
    // Phase 2 (size guide): per-open guidance context, never cached across opens.
    //   { data (island), sizeIdx (size option in product.options, -1 none), chart, fc,
    //     resolution (finder.resolve() | null = chart-only), viewed (chart opened),
    //     evaluated (logic asset ran), pickId (explicit chip pick this open, X31) }
    sg: null,
    touched: false, // the shopper tapped a pill since loadProduct (§1.8 sel = tap)
    sgPre: false, // our pre-selection set the size and nothing changed it since (§1.8 sel = pre)
    // BOPIS: per-open fulfilment context — stock = the ?view=paex-stock map for THIS product
    // (null until it lands), selected/chosen = the row the shopper picked in the modal,
    // intercepted = the last bridge add was stopped by paexFulfil.gate (no error toast then)
    fulfil: { stock: null, stockFor: '', selected: null, chosen: false, intercepted: false },
  };

  // Product + recommendations payloads memoized per PATH (path is unique
  // per product AND locale) for the page lifetime — no refetch on reopen,
  // and warmable by the pointerdown prefetch below. Promises are cached so
  // an open during an in-flight fetch reuses it; failures clear their slot
  // so the next open retries.
  const productCache = new Map();
  const recsCache = new Map();
  // W3-B fix 21 (2026-08-02): synchronous mirror of every SETTLED recs
  // payload (recsCache stores promises — unreadable at render time). Lets
  // renderLoading decide the recs block's footprint at open: cached items
  // render immediately (one settled shell size), cached empty stays
  // hidden (no dead band), and only a genuinely unknown open reserves.
  const recsSettled = new Map();
  const RECS_OPEN = '[[PAEXQARECS]]';
  const RECS_CLOSE = '[[/PAEXQARECS]]';
  // Size guide (2026-10-02): chart island per PATH, same memo/retry contract
  // as the recs; never requested while the theme kill switch is off (the
  // dialog then carries no data-paex-sg-src), and only for products whose
  // tags pass the chart test (isChartTag = the resolve snippet's pre-filter)
  // OR whose recs payload reports the paex.size_chart metafield set
  // (sg_mf) — so an uncharted product never pays the request.
  const sgCache = new Map();
  const SG_OPEN = '[[PAEXSG]]';
  const SG_CLOSE = '[[/PAEXSG]]';
  // Phase 2: synchronous mirror of every SETTLED island (as recsSettled) — the first
  // render can resolve + pre-select when warm() already fetched it; failures stay unknown.
  const sgSettled = new Map();

  const productFor = (path) => {
    if (productCache.has(path)) return productCache.get(path);
    const promise = fetch(path + '.js', { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error('product fetch failed');
      return response.json();
    });
    promise.catch(() => productCache.delete(path));
    productCache.set(path, promise);
    return promise;
  };

  const modal = () => document.getElementById('Paex-Quick-Add');
  // ONE switch signal: snippets/paex-quick-add.liquid emits data-paex-sg-src
  // only while settings.paex_sg_enabled is on (feature on + where the
  // renderer lives); absent ⇒ no size-guide request, no script injection.
  const sgEnabled = () => { const d = modal(); return !!(d && d.dataset.paexSgSrc); };
  const q = (sel) => modal() && modal().querySelector(sel);

  // W3.4: prices flow through the shop's money_format (webvista.
  // formatPriceAmount consumes window.priceFormatTemplate) so the modal
  // matches every other price on the page. M5 (2026-08-03): the ladder lives
  // in paex-cart-bridge.js (emitted right before this file, deferred);
  // call-time lookup, bare last-resort string when absent.
  const money = (cents) => {
    const b = window.PaexCartBridge;
    return b ? b.money(cents) : '$' + (cents / 100).toFixed(2);
  };

  const imageUrl = (src, width) => {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (error) {
      return src;
    }
  };

  /* ---------------- rendering (DOM building only) ---------------- */

  const clear = (node) => { while (node.firstChild) node.removeChild(node.firstChild); };

  // Availability row, colour-segmented (merchant 2026-07-31 + design crop):
  // the dot and the STATUS segment carry the state colour; the rest of
  // the sentence ("online", "at {store}") stays standard ink. Pass rest=''
  // for status-only rows (prompts, errors). W3.4: the glyph is the cards'
  // 6px round dot (CSS ::before on the row, currentColor — same component
  // as .rcc-product-inventory__row; the sheet has no ✓/× glyphs), so no
  // text mark is rendered here and a state can never ship the wrong mark
  // (the old error row drew ✓).
  const row = (kind, status, rest) => {
    const line = document.createElement('div');
    line.className = 'paex-quick-add__row';
    line.dataset.kind = kind;
    const statusSpan = document.createElement('span');
    statusSpan.textContent = status;
    line.append(statusSpan);
    if (rest) {
      const restSpan = document.createElement('span');
      restSpan.dataset.rest = '';
      restSpan.textContent = rest;
      line.append(restSpan);
    }
    return line;
  };

  // Skeleton state (fix 2026-07-31 round 7 — "opens in 2 segments"): the
  // shell opens at a settled layout, so the only motion is the flyout
  // itself. Reserved: 4 media tiles, a price bar and one pill row of
  // placeholders (the availability band already reserves 40px in CSS).
  const renderLoading = (cardTitle) => {
    q('[data-paex-qa-title]').textContent = cardTitle || '';
    const details = q('[data-paex-qa-details]');
    if (details) details.hidden = true;
    q('[data-paex-qa-price]').textContent = '';
    q('[data-paex-qa-price]').removeAttribute('data-reduced');
    const compare = q('[data-paex-qa-compare]');
    compare.hidden = true;
    compare.textContent = '';
    const price = q('[data-paex-qa-price]');
    const pricePh = document.createElement('span');
    pricePh.className = 'paex-quick-add__ph paex-quick-add__ph--price';
    price.append(pricePh);
    const media = q('[data-paex-qa-media]');
    clear(media);
    for (let i = 0; i < 4; i++) {
      const ph = document.createElement('span');
      ph.className = 'paex-quick-add__media-ph';
      media.append(ph);
    }
    const options = q('[data-paex-qa-options]');
    clear(options);
    const labelPh = document.createElement('div');
    labelPh.className = 'paex-quick-add__option-label';
    const labelBar = document.createElement('span');
    labelBar.className = 'paex-quick-add__ph paex-quick-add__ph--label';
    labelPh.append(labelBar);
    const pillsPh = document.createElement('div');
    pillsPh.className = 'paex-quick-add__pills';
    for (let i = 0; i < 4; i++) {
      const pill = document.createElement('span');
      pill.className = 'paex-quick-add__pill paex-quick-add__pill--ph';
      pillsPh.append(pill);
    }
    options.append(labelPh, pillsPh);
    // Fix 21 (2026-08-02, "desktop panel grows 497→560 after the flyout"):
    // the recs block's footprint is decided HERE, at open, instead of
    // whenever the section fetch lands — see reserveRecs.
    const recs = q('[data-paex-qa-recs]');
    if (recs) reserveRecs(recs);
    const rows = q('[data-paex-qa-rows]');
    rows.hidden = false;
    clear(rows);
    rows.append(row('p', t('checking', 'Checking…', 'Vérification…')));
    // BOPIS: a swap / back never shows the previous product's service rows
    const services = q('[data-paex-qa-services]');
    if (services) services.hidden = true;
    const qtyLine = q('[data-paex-qa-fulfil-qty]');
    if (qtyLine) qtyLine.hidden = true;
    // Size chart (2026-08-04): collapse + empty the mount at every load so
    // a swap/back never shows the previous product's chart while fetching;
    // renderSizeRow rebuilds it (or leaves it hidden) once the product
    // lands. No reserve for the row: the collapsed line is 20px and the
    // chart itself only exists after a deliberate expand (law 0.2). Phase 2:
    // the reset also empties the guidance slot + drops the stacked drawer.
    resetSizeRow();
    // Phase 2 slot reserve (reserveRecs shape): unknown island → the profile row + the recommendation
    // card (slot pad 10 + row 32 + gap 8 + card [2 border + 20 pad + 12.6 kicker + 4 + 26.4 size + 4 + 18
    // reason] = 137px at the 13px modal type) for profile holders only — a no-profile shopper's slot stays
    // hidden until intent (ruling 7), so a reserve for them could only collapse; known → exact; mobile sheet (88dvh) → none.
    if (sgSlot && sgEnabled() && sgHasProfiles() && window.matchMedia('(min-width: 768px)').matches && !sgSettled.has(state.productPath)) {
      sgSlot.style.minHeight = '137px';
      sgSlot.hidden = false;
      options.insertBefore(sgSlot, pillsPh);
    }
    let cta = q('[data-paex-qa-cta]');
    // W1 B4: leaving a notify state (re-open on another product) must shed
    // the app-bound node, not just the class — swap to a fresh CTA.
    if (cta.classList.contains('BIS_trigger')) cta = swapCta(cta);
    cta.disabled = true;
    cta.textContent = t('selectSize', 'Select a size', 'Choisissez une taille');
  };

  const matchesOtherSelections = (variant, optionIndex) => {
    for (let i = 0; i < state.selections.length; i++) {
      if (i === optionIndex) continue;
      const chosen = state.selections[i];
      if (chosen !== null && variant['option' + (i + 1)] !== chosen) return false;
    }
    return true;
  };

  const valueAvailable = (optionIndex, value) =>
    state.product.variants.some(
      (variant) =>
        variant['option' + (optionIndex + 1)] === value &&
        matchesOtherSelections(variant, optionIndex) &&
        variant.available
    );

  const currentVariant = () => {
    if (!state.product) return null;
    if (state.product.variants.length === 1) return state.product.variants[0];
    if (state.selections.some((value) => value === null)) return null;
    return (
      state.product.variants.find((variant) =>
        state.selections.every((value, i) => variant['option' + (i + 1)] === value)
      ) || null
    );
  };

  const renderPrice = () => {
    const variant = currentVariant();
    const priceCents = variant ? variant.price : state.product.price;
    const compareCents = variant ? variant.compare_at_price : state.product.compare_at_price;
    const price = q('[data-paex-qa-price]');
    price.textContent = money(priceCents);
    const compare = q('[data-paex-qa-compare]');
    if (compareCents && compareCents > priceCents) {
      price.setAttribute('data-reduced', '');
      compare.hidden = false;
      compare.textContent = money(compareCents);
    } else {
      price.removeAttribute('data-reduced');
      compare.hidden = true;
      compare.textContent = '';
    }
  };

  const renderRows = () => {
    const rows = q('[data-paex-qa-rows]');
    clear(rows);
    // BOPIS: with the service rows shown, the per-service rows ARE the availability answer —
    // the plain online / store rows would say the same thing twice (hidden, not removed: the
    // add-error row still lands here and un-hides it)
    rows.hidden = fulfilShown();
    const variant = currentVariant();
    if (!variant) {
      rows.append(
        row(
          'p',
          t('selectSizePrompt', 'Select a size to see availability', 'Choisissez une taille pour voir la disponibilité')
        )
      );
      return;
    }
    const online = t('online', 'online', 'en ligne');
    if (variant.available) {
      rows.append(row('g', t('inStock', 'In stock', 'En stock'), online));
    } else {
      // OOS ONLINE is selectable (merchant 2026-07-31): the row answers
      // for THIS size online; the store row below still answers for the
      // product in store — out of stock online ≠ out of stock in store.
      rows.append(row('m', t('outOfStock', 'Out of stock', 'Rupture de stock'), online));
    }
    // Store row: product-level answer from the PLP's cached proxy lookup
    // (per-size store stock is not exposed by /products/{handle}.js — the
    // design's per-size store row is flagged as a data gap).
    try {
      const placeRaw = (window.Nimstrata && window.Nimstrata.placeId) || '';
      const placeId = String(placeRaw).split('/').pop();
      if (placeId && typeof rccInventoryCache !== 'undefined' && typeof rccInventoryStoreName === 'function') {
        const cached = rccInventoryCache.get(placeId + ':' + String(state.product.id));
        const store = rccInventoryStoreName();
        if (cached === true && store) {
          rows.append(row('g', t('inStock', 'In stock', 'En stock'), t('atStore', 'at', 'à') + ' ' + store));
        } else if (cached === false && store) {
          rows.append(row('n', t('notCarried', 'Not carried', 'Non offert'), t('atStore', 'at', 'à') + ' ' + store));
        }
      }
    } catch (error) { /* store row is additive */ }
  };

  // W1 B4 (2026-08-01): the notify CTA was a dead click. TWO causes,
  // root-caused against the live widget source (backinstock.useamp.com
  // widget/37799_*.js):
  // 1. The BIS app handles .BIS_trigger via ONE delegated click listener
  //    on document (BIS.genericTriggerHandler: matches
  //    event.target.className against /BIS_trigger/, reads
  //    data-product-handle + data-variant-id, then popover.form.show).
  //    Ascent's ModalDialog stopPropagation()s EVERY click inside a
  //    modal (global.js ~1886), so no click inside our quick-add modal
  //    can ever reach that delegate — the card-level trigger
  //    (rcc-card-components.js) only works because it lives outside any
  //    modal. Bridge: the CTA's own listener (which runs on the node,
  //    before bubbling dies at the modal boundary) invokes
  //    BIS.genericTriggerHandler(event) directly when in notify state —
  //    same handler, same event, same dedupe (the app caches popovers
  //    per product handle, so repeated open/close cannot
  //    double-register).
  // 2. Node hygiene per the card-level pattern (rcc-product-notify-me
  //    renders a FRESH button.BIS_trigger per render): clone-and-swap
  //    the CTA on every notify transition, in AND out, so each state
  //    change lands as a virgin node and anything the app attached to
  //    the old node leaves the DOM with it. wire()'s original addToCart
  //    binding dies with the replaced node — re-bound here; available-
  //    variant clicks keep flowing through addToCart exactly as before.
  const onCtaClick = (event) => {
    const target = event.currentTarget;
    if (target && target.classList.contains('BIS_trigger')) {
      try {
        if (window.BIS && typeof window.BIS.genericTriggerHandler === 'function') {
          window.BIS.genericTriggerHandler(event);
        }
      } catch (error) { /* notify is additive — never break the modal */ }
      return; // addToCart would no-op on the unavailable variant anyway
    }
    addToCart();
  };

  const swapCta = (cta) => {
    const fresh = cta.cloneNode(false);
    fresh.classList.remove('BIS_trigger', 'paex-quick-add__cta--notify');
    delete fresh.dataset.productHandle;
    delete fresh.dataset.variantId;
    fresh.addEventListener('click', onCtaClick);
    cta.replaceWith(fresh);
    return fresh;
  };

  const renderCta = () => {
    let cta = q('[data-paex-qa-cta]');
    const variant = currentVariant();
    const wantNotify = !!(variant && !variant.available);
    // Reset any notify wiring from a previous selection before restyling —
    // via node swap whenever the notify state is entered OR left, so the
    // BIS app always sees a fresh trigger and never a recycled node.
    if (wantNotify || cta.classList.contains('BIS_trigger')) {
      cta = swapCta(cta);
    }
    delete cta.dataset.fulfilReason;
    if (variant && variant.available) {
      cta.disabled = false;
      const suffix = state.product.variants.length > 1 ? ' · ' + state.selections.filter(Boolean).join(' / ') : '';
      // BOPIS: the label follows the selected service (add_pickup {store} / add_local; ship keeps
      // today's "Add to bag"); a size the service cannot fulfil disables the CTA with the reason
      // (unavailable_{service}_variant), the same combined state as the PDP button.
      const fulfilCta = fulfilCtaSpec(variant);
      if (fulfilCta && fulfilCta.disabled) {
        cta.disabled = true;
        cta.dataset.fulfilReason = fulfilCta.service;
        cta.textContent = fulfilCta.text;
      } else if (fulfilCta && fulfilCta.text) {
        cta.textContent = fulfilCta.text + suffix;
      } else {
        cta.textContent = t('addToBag', 'Add to bag', 'Ajouter au sac') + suffix;
      }
    } else if (variant) {
      // OOS-online size selected (merchant 2026-07-31): mirror the card's
      // sold-out recovery (design D-24) — the CTA becomes the outlined
      // "Notify me when available" pill wired as a BIS trigger (the
      // Back-In-Stock app delegates clicks on .BIS_trigger and reads
      // data-product-handle/data-variant-id, same contract as
      // rcc-product-notify-me). addToCart() ignores the click because the
      // variant is unavailable. The design stages no explicit modal state
      // for a selected OOS size — card behaviour mirrored, flagged.
      cta.disabled = false;
      cta.classList.add('BIS_trigger', 'paex-quick-add__cta--notify');
      cta.dataset.productHandle = state.product.handle || '';
      cta.dataset.variantId = String(variant.id);
      cta.textContent = t('notifyMe', 'Notify me', 'Prévenez-moi'); // W3.7 short form, matches locales
    } else {
      cta.disabled = true;
      cta.textContent = t('selectSize', 'Select a size', 'Choisissez une taille');
    }
  };

  const renderOptions = () => {
    const wrap = q('[data-paex-qa-options]');
    clear(wrap);
    if (state.product.variants.length === 1) return; // direct-add products never open this, but guard anyway
    // Phase 2: badge = the ACTIVE profile's GREEN size on the size option (X36: profile
    // required; island settings.badge may switch it off); the Result is finder.resolve()'s.
    const sg = state.sg;
    const res = sg && sg.resolution;
    const R = res && res.result;
    const sgSettings = (sg && sg.data.settings) || {};
    // slot home: the island's size option; while the island is unknown the reserve
    // sits under the name-test option so it survives this render (R10)
    const slotIdx = sg ? sg.sizeIdx : (sgSlot && !sgSlot.hidden && sgSlot.style.minHeight ? sgReserveIndex() : -1);
    state.product.options.forEach((option, optionIndex) => {
      const label = document.createElement('div');
      label.className = 'paex-quick-add__option-label';
      label.textContent = option.name;
      const pills = document.createElement('div');
      pills.className = 'paex-quick-add__pills';
      pills.setAttribute('role', 'group');
      pills.setAttribute('aria-label', option.name);
      // reserve the badge line on the size option's rows whenever a badge is drawn
      const badgeOn = !!(R && R.badge && R.size && res.profile && sgSettings.badge !== false && optionIndex === sg.sizeIdx);
      pills.classList.toggle('paex-quick-add__pills--badged', badgeOn);
      option.values.forEach((value) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'paex-quick-add__pill';
        pill.textContent = value;
        pill.dataset.value = value; // stable identity for focus continuity (textContent now includes the badge)
        const badged = badgeOn && value === R.size;
        if (badged) {
          // design 1.15 "✓ Léo" chip above the pill: decorative (aria-hidden); the
          // accessible name stays the size, the description names the person (§8)
          pill.classList.add('paex-quick-add__pill--rec');
          const badge = document.createElement('span');
          badge.className = 'paex-quick-add__pill-badge';
          badge.setAttribute('aria-hidden', 'true');
          badge.textContent = sgStr('badge_tag', { name: sgName(res.profile) });
          pill.append(badge);
          pill.setAttribute('aria-describedby', SG_ID_PREFIX + 'Desc');
        }
        pill.setAttribute('aria-pressed', String(state.selections[optionIndex] === value));
        if (!valueAvailable(optionIndex, value)) {
          // Sold out renders struck in place, not hidden (E-01) — but stays
          // SELECTABLE (merchant 2026-07-31: out of stock online does not
          // mean out of stock in store). Selecting reveals that size's
          // rows and swaps the CTA to the notify pill.
          pill.classList.add('paex-quick-add__pill--oos');
        } else {
          // BOPIS (6b): ONE combined unavailable state per service — a size every matching
          // variant cannot fulfil for the selected service is struck too, still selectable,
          // its reason read with the pill (visually hidden text, unavailable_{service}_variant)
          const outReason = fulfilPillReason(optionIndex, value);
          if (outReason) {
            pill.classList.add('paex-quick-add__pill--fulfil-out');
            const sr = document.createElement('span');
            sr.className = 'visually-hidden';
            sr.textContent = ', ' + outReason;
            pill.append(sr);
          }
        }
        pill.addEventListener('click', () => {
          // the tap wins over a late island (R3) and ends the "pre" attribution (§1.8)
          state.touched = true;
          state.sgPre = false;
          selectValue(optionIndex, value, true);
          if (badged && state.selections[optionIndex] === value) {
            sgTrack('recommendation_applied', { handle: sg.data.chart.handle, size: value, tier: R.tier, how: 'pill' });
          }
        });
        pills.append(pill);
      });
      // design order: label → guidance slot → pills; the slot is ONE persistent node
      // moved here (clear() detached it), its content owned by renderGuidance
      if (optionIndex === slotIdx && sgSlot && !sgSlot.hidden) wrap.append(label, sgSlot, pills);
      else wrap.append(label, pills);
    });
  };

  // FOCUS CONTINUITY (2026-08-01 regression runner): the click re-renders the pill
  // and removing the focused node drops focus to <body> — ESC then silently died
  // (ModalDialog hears keyup on itself). Re-focus the fresh pill by data-value
  // (R1: the badge span sits inside the pill, textContent is no longer the value).
  const refocusPill = (optionIndex, value) => {
    const groups = modal().querySelectorAll('.paex-quick-add__pills');
    const group = groups[optionIndex];
    const fresh = group && Array.from(group.querySelectorAll('.paex-quick-add__pill')).find((p) => p.dataset.value === value);
    if (fresh) fresh.focus({ preventScroll: true });
  };

  // ONE selection path for the pill click (toggle), the drawer's "Choisir", the
  // next-size card and the boxes; a change of ANOTHER option (colour) re-resolves
  // against the new per-size availability (X26, never cached).
  const selectValue = (optionIndex, value, toggle) => {
    state.selections[optionIndex] = toggle && state.selections[optionIndex] === value ? null : value;
    if (state.sg && state.sg.resolution && optionIndex !== state.sg.sizeIdx) sgRefresh();
    renderGuidance();
    renderServices(); // BOPIS: the rows answer for the new size before the pills / CTA read the selection
    renderOptions();
    renderPrice();
    renderRows();
    renderCta();
    refocusPill(optionIndex, value);
    sgUpdateDesc();
  };

  /* ---------------- Size chart row (native size guide Phase 1, 2026-10-02) ----
   * Replaces the MP Size Chart mirror (Option A, 2026-08-04 → quarantine).
   * Same mount [data-paex-qa-size-chart] (OUTSIDE the options container —
   * renderOptions wipes that one on every pill click), same collapsed
   * "Tableau des tailles" row. Phase 2 (2026-10-03): the row's toggle first
   * opens OUR drawer stacked over the quick-add (design 1.15b); the Phase 1
   * inline table, built LAZILY on first expand (law 0.2), stays as the
   * degrade path. Data: the paex-size-guide-data section render (chart JSON
   * between sentinels), gated on the kill switch (data-paex-sg-src present
   * only while settings.paex_sg_enabled is on), then on the product's tags
   * or the recs payload's sg_mf flag, memoized per path; the table itself is
   * drawn by the PDP's renderer (assets/paex-size-guide.js, injected once
   * from the dialog's data-paex-sg-src) in the shopper's preferred unit.
   * EVERYTHING is wrapped in try/catch: any failure or no chart ⇒ the mount
   * stays hidden and the modal is unaffected.
   * ------------------------------------------------------------------- */

  // the resolve snippet's pre-filter (snippets/paex-size-guide-resolve.liquid)
  const isChartTag = (tag) => {
    const t = String(tag || '').toLowerCase();
    return t.indexOf('size') !== -1 || t.indexOf('china-costume-') === 0 || t.indexOf('bioworld_') === 0;
  };

  // Chart island for a product path; resolves null BEFORE any request when
  // the kill switch is off (nothing cached in that state) or when neither
  // the tags nor the recs flag (sgMf = paex.size_chart set, the one case the
  // client cannot see) claim a chart; null too when the section renders
  // nothing (no chart) or on failure (slot cleared so a later open retries).
  // Shopify.routes.root aware: productPath already carries the locale
  // prefix (it comes from the card's own anchor).
  const sgFor = (productPath, product, sgMf) => {
    if (!sgEnabled()) return Promise.resolve(null);
    const tags = product && Array.isArray(product.tags) ? product.tags : [];
    if (!tags.some(isChartTag) && sgMf !== true) return Promise.resolve(null);
    if (sgCache.has(productPath)) return sgCache.get(productPath);
    const promise = fetch(`${productPath}?section_id=paex-size-guide-data`)
      .then((response) => {
        if (!response.ok) throw new Error('size guide fetch failed');
        return response.text();
      })
      .then((html) => {
        const start = html.indexOf(SG_OPEN);
        const end = html.lastIndexOf(SG_CLOSE); // last: a cell may quote the sentinel text
        if (start === -1 || end === -1 || end <= start) return null;
        return JSON.parse(html.slice(start + SG_OPEN.length, end));
      })
      .then((data) => {
        sgSettled.set(productPath, data); // Phase 2: known from now on (null = settled "no chart")
        return data;
      })
      .catch(() => {
        sgCache.delete(productPath); // allow a retry on the next open
        sgSettled.delete(productPath); // errors stay UNKNOWN, not "no chart"
        return null;
      });
    sgCache.set(productPath, promise);
    return promise;
  };

  // The island for a path once the product payload is known: a chart tag
  // starts the request at once; otherwise wait for the (memoized, parallel)
  // recs payload and honour its sg_mf flag — a missing / failed / old-shape
  // payload (no sg_mf key) falls back to the tag test, never blocks the row.
  const sgWhen = (productPath, product) => {
    const tags = product && Array.isArray(product.tags) ? product.tags : [];
    if (!sgEnabled() || tags.some(isChartTag)) return sgFor(productPath, product, false);
    return recsFor(productPath)
      .catch(() => null)
      .then((payload) => {
        // new-shape payload with sg_mf === false (no tag either) = settled "no chart" (the
        // reserve never fires again for it); failed / old-shape stays UNKNOWN
        if (payload && payload.sg_mf === false && !sgSettled.has(productPath)) sgSettled.set(productPath, null);
        return sgFor(productPath, product, !!(payload && payload.sg_mf === true));
      });
  };

  // The "_Size advice" encoder (assets/paex-size-advice.js, 2026-10-05) loads on intent with the
  // base — never on a plain collection view; its URL rides on the dialog (data-paex-sg-advice-src).
  // Resolves null when absent or failed: the add then posts no property and the cart backstop stamps.
  let sgAdvicePromise = null;
  const ensureSgAdvice = () => {
    if (window.paexSizeAdvice) return Promise.resolve(window.paexSizeAdvice);
    if (sgAdvicePromise) return sgAdvicePromise;
    const dialog = modal();
    const src = dialog && dialog.dataset.paexSgAdviceSrc;
    if (!src) return Promise.resolve(null);
    sgAdvicePromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(window.paexSizeAdvice || null);
      script.onerror = () => {
        sgAdvicePromise = null; // allow a retry on the next charted open
        resolve(null);
      };
      document.head.append(script);
    });
    return sgAdvicePromise;
  };

  // Inject assets/paex-size-guide.js once (the PDP's renderer + logic); the
  // URL rides on the dialog (data-paex-sg-src, emitted by the snippet).
  let sgScriptPromise = null;
  const ensureSg = () => {
    ensureSgAdvice();
    if (window.paexSizeGuide) return Promise.resolve(window.paexSizeGuide);
    if (sgScriptPromise) return sgScriptPromise;
    const dialog = modal();
    const src = dialog && dialog.dataset.paexSgSrc;
    if (!src) return Promise.reject(new Error('size guide asset unknown'));
    sgScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => (window.paexSizeGuide ? resolve(window.paexSizeGuide) : reject(new Error('size guide asset empty')));
      script.onerror = () => {
        sgScriptPromise = null; // allow a retry on the next expand
        reject(new Error('size guide asset failed'));
      };
      document.head.append(script);
    });
    return sgScriptPromise;
  };

  const resetSizeChart = () => {
    const mount = q('[data-paex-qa-size-chart]');
    if (!mount) return;
    mount.hidden = true;
    clear(mount);
    delete mount.dataset.chartFor;
  };

  /* ---------------- Size guide Phase 2 (2026-10-03; BUILD-PLAN §1.4 / §2 WP4) ----
   * Codes against window.paexSizeGuide: base CORE (store/track/ensure), the finder
   * (chart/resolve = the ONE engine), render.row (rec asset) and render.drawer
   * (drawer asset) — each guarded, absent ⇒ chart row only. GREEN-only pre-selection
   * on payload availability (X26), profile required (X36), finder false ⇒ chart-only
   * (X28), cross pick never persisted (X31), events = §1.9 with src 'qa'.
   * ------------------------------------------------------------------- */
  const SG_STACKED_ID = 'Paex-Quick-Add-Size-Guide';
  const SG_ID_PREFIX = 'Paex-Quick-Add-Sg-';
  const SG_CLASS_PREFIX = 'paex-quick-add__sg-';
  const noop = () => {};
  const nz = (s) => String(s == null ? '' : s).trim().toLowerCase();
  const qaOpen = () => { const d = modal(); return !!(d && d.hasAttribute('open')); };
  const sgApi = () => window.paexSizeGuide || null;
  const sgLogicReady = () => { const SG = sgApi(); return !!(SG && SG.finder && SG.adapter && SG.store && SG.track && SG.logic && typeof SG.logic.policy === 'function'); };
  let sgSlot = null; // ONE persistent guidance node (created in wire), moved by renderOptions, filled by renderGuidance
  let sgDrawerCtl = null; // render.drawer controller for the current product + mode
  let sgInlineExpand = null; // the Phase 1 inline expand of the current row (degrade path)
  let sgReturn = null; // { el, token }: where focus goes back after the stacked drawer closes
  let sgAnnounced = false; // live-region pre-selection announcement once per loadProduct
  let sgSwitchEmitted = null; // Set of 'group|profile|how' already emitted this loadProduct (G6)
  let sgDrawerCtxObj = null; // the ONE ctx object the live drawer controller reads (WP3 re-reads sizes/selected from it on refresh)
  let sgAskEmitted = false; // finder_start {from:'ask'} once per loadProduct (§1.9)
  const SG_SWITCH_MODE = { explicit: 'manual', auto: 'auto', mru: 'mru' }; // Resolution.how → profile_switch.mode (events.json enum)
  const SG_HOW_ROW = { box: 1, next: 1, largest: 1 }; // X32: how values a row control hands over
  const SG_HOW_DRAWER = { drawer: 1, largest: 1 }; // X32: how values the stacked drawer hands over
  const SG_QCODE = { 'height,weight': 'hw', 'chest,waist': 'cw', height: 'h', chest: 'c', age: 'age' }; // finder_start.questions enum

  // island strings only (never JS literals); {x} substitution as SG.logic.t does
  const sgStr = (key, vars) => {
    const strings = (state.sg && state.sg.data && state.sg.data.strings) || {};
    const s = strings[key] != null ? String(strings[key]) : key;
    return vars ? s.replace(/\{(\w+)\}/g, (m, x) => (vars[x] != null ? vars[x] : m)) : s;
  };
  // the badge always names the person: first name, else the type label (Enfant / Adulte / Bébé)
  const sgName = (profile) => (profile && (profile.name || sgStr(profile.type))) || '';
  // read-only peek used before any asset is loaded (never writes; harmless in designMode)
  const sgHasProfiles = () => {
    try {
      const v = JSON.parse(localStorage.getItem('paex:sg:profiles') || '[]');
      return Array.isArray(v) && v.length > 0;
    } catch (error) { return false; }
  };
  // analytics gate (§1.9): the quick-add dialog carries data-analytics +
  // data-src="qa" while the island says settings.analytics; base track()
  // reads both, so every event (incl. the base's own `level`) says src 'qa'.
  const sgGate = () => {
    const dialog = modal();
    if (!dialog) return false;
    const on = !!(state.sg && state.sg.data && state.sg.data.settings && state.sg.data.settings.analytics === true);
    if (on) { dialog.setAttribute('data-analytics', ''); dialog.dataset.src = 'qa'; } else dialog.removeAttribute('data-analytics');
    return on ? dialog : false;
  };
  const sgTrack = (name, payload) => {
    try {
      const SG = sgApi();
      if (SG && SG.track) SG.track.event(sgGate(), name, payload || {});
    } catch (error) { /* analytics is additive */ }
  };

  // the size option: the island's option_index (same order as /products/{handle}.js),
  // else best value overlap (case-insensitive), else the name test (6 misnamed live)
  const sgSizeOptionIndex = (product, data) => {
    const opts = product.options || [];
    const d = data && data.product;
    if (d && Number.isInteger(d.option_index) && opts[d.option_index]) return d.option_index;
    const vals = (d && Array.isArray(d.options) ? d.options : []).map(nz);
    let best = -1;
    let bestHits = 0;
    opts.forEach((o, i) => {
      const hits = (o.values || []).filter((v) => vals.indexOf(nz(v)) !== -1).length;
      if (hits > bestHits) { best = i; bestHits = hits; }
    });
    if (best === -1) best = opts.findIndex((o) => /size|taille|grandeur/i.test(o.name || ''));
    return best;
  };
  // the reserve's home before the island is known: the name test, else the first option
  const sgReserveIndex = () => { const i = (state.product.options || []).findIndex((o) => /size|taille|grandeur/i.test(o.name || '')); return i < 0 ? 0 : i; };

  // X26 shape [{ v, id, a, skuBlank }] from the payload: a = valueAvailable (offered
  // AND available for the current other selections; the payload wins — no Shopify
  // pills here); id = first matching variant; recomputed on every call.
  const sgSizes = () => {
    const sg = state.sg;
    if (!sg || sg.sizeIdx < 0 || !state.product) return [];
    const i = sg.sizeIdx;
    const option = state.product.options[i] || {};
    return (option.values || []).map((v) => {
      const variant = state.product.variants.find((x) => x['option' + (i + 1)] === v && matchesOtherSelections(x, i)) || null;
      return { v, id: variant ? variant.id : null, a: valueAvailable(i, v), skuBlank: false };
    });
  };
  const sgSizeOf = (value) => sgSizes().find((x) => x.v === value) || null;
  // the recommended row's FULL label ("M* (7-8)") for SR text, as the PDP's live region (parity: paex-size-rec.js); falls back to the option value
  const sgRowLabel = (sg, R) => { const SG = sgApi(); try { return SG && SG.logic && sg && sg.fc && typeof R.idx === 'number' ? SG.logic.sizeLabel(sg.fc, R.idx, sgLang()) : String(R.size); } catch (e) { return String(R.size); } };
  const sgVariantIdFor = (size) => { const s = size != null ? sgSizeOf(size) : null; return s && s.id != null ? String(s.id) : ''; };
  const sgLang = () => { const SG = sgApi(); return SG && typeof SG.lang === 'function' ? SG.lang() : (isFr() ? 'fr' : 'en'); };
  const sgUnit = (data, chart) => { const SG = sgApi(); return SG.adapter.readUnit() || SG.adapter.prefFor(data, chart); };

  // profile_switch once per loadProduct per (groupKey, profileId, how) — G6; only a routed
  // Resolution (how explicit|auto|mru) is a switch — a save / anonymous run (how null) never
  // is; `seen` = the drawer already emitted it for its own chip (§1.9) → remember, no re-emit
  const sgEmitSwitch = (res, seen) => {
    if (!res || res.status !== 'active' || !res.profile || !sgSwitchEmitted || typeof res.groupKey !== 'string' || !SG_SWITCH_MODE[res.how]) return;
    const key = res.groupKey + '|' + res.profile.id + '|' + res.how;
    if (sgSwitchEmitted.has(key)) return;
    sgSwitchEmitted.add(key);
    if (!seen) sgTrack('profile_switch', { mode: SG_SWITCH_MODE[res.how], group: res.groupKey, n_eligible: (res.eligible || []).length });
  };
  const sgProfileId = (res) => (res && res.profile ? res.profile.id : null);
  // the latest Result on this surface (= the PDP's inst.result): a drawer run for someone
  // else (profile null) supersedes the profile's for the offers, the stamp and `tier` (X36)
  const sgCurrentResult = () => { const sg = state.sg; return sg ? sg.anonResult || (sg.resolution && sg.resolution.result) || null : null; };
  // finder.resolve with the §1.4 ctx; explicitId = a chip pick (resolve writes
  // active_by_group itself for an eligible pick, never for a cross pick — G23)
  const sgResolve = (explicitId) => {
    const sg = state.sg;
    const ctx = { data: sg.data, chart: sg.chart, fc: sg.fc, sizes: sgSizes(), lang: sgLang(), unit: sgUnit(sg.data, sg.chart) };
    if (explicitId != null) ctx.explicitId = explicitId;
    return sgApi().finder.resolve(ctx);
  };
  // re-resolve the current pick against fresh sizes (other-option change)
  const sgRefresh = () => {
    const sg = state.sg;
    if (!sg || !sg.resolution || !sgLogicReady()) return;
    try { sg.resolution = sgResolve(sg.pickId); } catch (error) { /* keep the previous resolution */ }
  };

  // adapter.adapt → finder.chart → finder.resolve. Chart-only (resolution null) when
  // the logic asset is absent, no size option / single variant, settings.finder false
  // (X28) or nothing to ask; `viewed`/`pickId` survive a re-prepare of the same island.
  const sgPrepare = (data) => {
    const prev = state.sg;
    state.sg = null;
    try {
      if (!sgEnabled() || !state.product || !data || !data.chart || !Array.isArray(data.chart.rows) || !data.chart.rows.length) return;
      const sizeIdx = sgSizeOptionIndex(state.product, data);
      const sg = { data, sizeIdx, chart: null, fc: null, resolution: null, anonResult: null, viewed: !!(prev && prev.data === data && prev.viewed), evaluated: false, pickId: prev && prev.data === data ? prev.pickId : null };
      state.sg = sg;
      if (!sgLogicReady() || sizeIdx < 0 || state.product.variants.length < 2) return; // chart row still renders; no guidance
      const SG = sgApi();
      sg.chart = SG.adapter.adapt(data); // idempotent (G34); loads the island strings for SG.logic.t
      sg.fc = SG.finder.chart(sg.chart, data);
      sg.evaluated = true;
      const s = data.settings || {};
      if (s.finder === false || !SG.logic.policy(sg.fc).ask.length) return; // X28 / nothing to ask → chart-only
      sg.resolution = sgResolve(sg.pickId);
      sgEmitSwitch(sg.resolution);
      if (sg.resolution.status === 'active') SG.store.bumpLevel('profile', sgGate()); // a saved profile in use = level profile (same rule as the base boot)
    } catch (error) {
      if (state.sg) { state.sg.resolution = null; state.sg.evaluated = true; }
    }
  };

  // Ruling 12 on the quick-add: not designMode, picker untouched + nothing chosen,
  // GREEN with a profile (X36), Result.preselect (stale ⇒ false, X17), the size offered
  // AND available with a known id (never sold out), settings.preselect on. The PDP-only
  // guards (clone, ?variant, userChoseVariant, delivery paint) have no PLP equivalent.
  const sgPreselect = () => {
    const sg = state.sg;
    const res = sg && sg.resolution;
    const R = res && res.result;
    if (!R || !res.profile || R.tier !== 'green' || !R.preselect || !R.size || sg.sizeIdx < 0) return false;
    if (window.Shopify && Shopify.designMode) return false;
    if (state.touched || state.sgPre || state.selections[sg.sizeIdx] !== null) return false;
    if ((sg.data.settings || {}).preselect === false) return false;
    const size = sgSizeOf(R.size);
    if (!size || size.a !== true || size.id == null) return false;
    state.selections[sg.sizeIdx] = R.size;
    state.sgPre = true;
    sgTrack('recommendation_applied', { handle: sg.data.chart.handle, size: R.size, tier: R.tier, how: 'pre' });
    return true;
  };

  // the pill click's renderers + the slot first (content before renderOptions moves it)
  const sgRerenderPills = () => {
    const active = document.activeElement;
    const keep = active && active.classList && active.classList.contains('paex-quick-add__pill')
      ? { idx: Array.from(modal().querySelectorAll('.paex-quick-add__pills')).findIndex((g) => g.contains(active)), value: active.dataset.value }
      : null;
    // a focused slot control (household chip / band / opener) is replaced by renderGuidance:
    // land on its successor, never on <body> (the PDP rec's focus(sel, alt))
    const inSlot = sgSlot && active && active !== sgSlot && sgSlot.contains(active) ? active : null;
    const slotSel = !inSlot ? null
      : inSlot.dataset.profileId != null ? ['[data-profile-id="' + CSS.escape(inSlot.dataset.profileId) + '"]', '.paex-quick-add__sg-change']
      : inSlot.classList.contains('paex-quick-add__sg-band') ? ['.paex-quick-add__sg-band', '.paex-quick-add__sg-find[data-paex-sg-ask], .paex-quick-add__sg-change']
      : inSlot.dataset.paexSgReturn ? ['[data-paex-sg-return="' + CSS.escape(inSlot.dataset.paexSgReturn) + '"]', '.paex-quick-add__sg-change, .paex-quick-add__sg-find']
      : null;
    renderGuidance();
    renderServices(); // BOPIS
    renderOptions();
    renderPrice();
    renderRows();
    renderCta();
    if (keep && keep.idx >= 0) refocusPill(keep.idx, keep.value);
    if (slotSel) {
      const next = sgSlot.querySelector(slotSel[0]) || sgSlot.querySelector(slotSel[1]);
      if (next && typeof next.focus === 'function') next.focus({ preventScroll: true });
    }
    sgUpdateDesc();
    sgAnnounce();
  };
  // "Recommandé pour Léo · M" in the quick-add's live region, once per loadProduct (§8)
  const sgAnnounce = () => {
    const sg = state.sg;
    const R = sg && sg.resolution && sg.resolution.result;
    const live = q('[data-paex-qa-live]');
    if (!live || sgAnnounced || !R || !state.sgPre || state.selections[sg.sizeIdx] !== R.size) return;
    sgAnnounced = true;
    live.textContent = sgStr('rec_for', { name: sgName(sg.resolution.profile) }) + ' · ' + sgRowLabel(sg, R);
  };

  // SR description for the badged pill: "Recommandé pour Léo[, Épuisé en ligne]" (§8)
  const sgUpdateDesc = () => {
    const desc = document.getElementById(SG_ID_PREFIX + 'Desc');
    if (!desc) return;
    const sg = state.sg;
    const res = sg && sg.resolution;
    const R = res && res.result;
    if (!R || !R.badge || !R.size || !res.profile) { desc.textContent = ''; return; }
    const parts = [sgStr('rec_for', { name: sgName(res.profile) }) + ' · ' + sgRowLabel(sg, R)];
    if (R.available === false) parts.push(sgStr('sold_out_online'));
    desc.textContent = parts.join(', ');
  };

  // a size chosen through the guide (X32 how values); the choice ends the "pre" attribution
  const sgApply = (value, how) => {
    const sg = state.sg;
    if (!sg || sg.sizeIdx < 0 || value == null) return;
    state.touched = true;
    state.sgPre = false;
    selectValue(sg.sizeIdx, value, false);
    const R = sgCurrentResult();
    sgTrack('recommendation_applied', { handle: sg.data.chart.handle, size: value, tier: R ? R.tier : '', how });
  };
  // pre-selection is boot-only (§1.4 rule c): a later person change never selects anew, and
  // our own untouched pre-selection never outlives the person it was made for (ruling 10;
  // the stamp's `pre` stays truthful)
  const sgDropPre = (prevId) => {
    const sg = state.sg;
    if (!sg || !state.sgPre || state.touched || sgProfileId(sg.resolution) === prevId) return;
    state.selections[sg.sizeIdx] = null;
    state.sgPre = false;
    sgAnnounced = false;
  };
  // household chip tapped: resolve with explicitId (eligible → written by resolve; cross →
  // state 9, never persisted, X31)
  const sgOnPick = (profileId) => {
    const sg = state.sg;
    if (!sg || !sgLogicReady()) return;
    const prevId = sgProfileId(sg.resolution);
    try {
      sg.pickId = profileId;
      sg.resolution = sgResolve(profileId);
    } catch (error) { return; }
    sg.anonResult = null;
    sgEmitSwitch(sg.resolution);
    sgDropPre(prevId);
    sgRerenderPills();
  };
  // state-14 band answered: written into the ACTIVE profile (updated_at un-stales it)
  // through store.writeProfiles, re-resolve, re-render — never a pre-selection (G16/G19)
  const sgOnAnswer = (role, band) => {
    const sg = state.sg;
    const SG = sgApi();
    const p = sg && sg.resolution && sg.resolution.profile;
    if (!p || !SG || !band || !sg.fc || !role) return;
    try {
      const list = SG.store.profiles();
      const cur = list.find((x) => x.id === p.id);
      if (!cur) return;
      const now = new Date().toISOString();
      cur[role] = band.value;
      cur.bands = cur.bands || {};
      cur.bands[role] = { lo: band.lo == null ? null : band.lo, hi: band.hi == null ? null : band.hi, chart: sg.fc.handle };
      cur.updated_at = now;
      cur.last_used_at = now;
      SG.store.writeProfiles(list);
      sg.resolution = sgResolve(sg.pickId);
      sg.anonResult = null;
    } catch (error) { return; }
    sgRerenderPills();
  };
  // our offers' notify button: the same BIS bridge as onCtaClick (the modal stops propagation)
  const sgOnNotify = (event) => {
    try {
      if (window.BIS && typeof window.BIS.genericTriggerHandler === 'function') window.BIS.genericTriggerHandler(event);
    } catch (error) { /* notify is additive — never break the modal */ }
    const sg = state.sg;
    const R = sg && sg.resolution && sg.resolution.result;
    sgTrack('notify_click', { handle: sg ? sg.data.chart.handle : '', size: R && R.size ? R.size : '' });
  };
  // the state-14 ask box opened: finder_start {from:'ask'} once per loadProduct (§1.9; the
  // PDP rec emits it once per page) — the box itself is render.row's
  const sgOnAsk = () => {
    const sg = state.sg;
    if (sgAskEmitted || !sg || !sg.resolution || !sg.fc) return;
    sgAskEmitted = true;
    const ask = sgApi().logic.policy(sg.fc).ask;
    sgTrack('finder_start', { handle: sg.data.chart.handle, group: sg.resolution.groupKey, questions: SG_QCODE[ask.join(',')] || SG_QCODE[ask[0]] || '', from: 'ask' });
  };
  // render.row ctx (§1.4.1 + the additive keys the PDP passes too — WP2J note: dialogId for
  // the openers' aria-controls, unit, result for the X36 offers, pending, profileId, hubUrl);
  // no host / section / delivery tiles / nudge card here (§6)
  const sgRowCtx = (sg) => {
    const s = sg.data.settings || {};
    const ctx = {
      surface: 'quick_add',
      classPrefix: SG_CLASS_PREFIX,
      idPrefix: SG_ID_PREFIX,
      sectionId: null,
      dialogId: SG_STACKED_ID,
      chart: sg.chart,
      fc: sg.fc,
      sizes: sgSizes(),
      selected: state.selections[sg.sizeIdx],
      unit: sgUnit(sg.data, sg.chart),
      pending: false,
      profileId: sgProfileId(sg.resolution),
      host: null,
      deliveryEl: null,
      bisPresent: () => !!((window.BIS && typeof window.BIS.genericTriggerHandler === 'function') || document.querySelector('button.BIS_trigger')),
      settings: { offers: s.offers !== false, nudge: false, badge: s.badge !== false, recCard: s.rec_card !== false, nudgeText: '', nudgeButton: '', sizeFilterUrl: s.size_filter_url || '', contactUrl: s.contact_url || '', hubUrl: s.hub_url || '', finder: s.finder !== false },
      onFind: (mode, opts) => openStackedDrawer(mode || 'finder', null, opts || null), // {from, profileId, view} → render.drawer open(opener, opts) (§1.4.2 ruling 1)
      onEdit: (profileId) => openStackedDrawer('finder', null, { from: 'edit', profileId, view: 'edit' }), // the row's Modifier → the stacked drawer's edit view
      onAsk: sgOnAsk,
      onAnswer: sgOnAnswer,
      onChange: noop,
      onPick: sgOnPick,
      onChoose: (value, from) => sgApply(value, SG_HOW_ROW[from] ? from : 'box'),
      onNotify: sgOnNotify,
      notifyVariantId: () => { const R = sgCurrentResult(); return sgVariantIdFor(R ? R.size : null); },
      productHandle: (state.product && state.product.handle) || '',
      onDismissNudge: noop,
      track: sgTrack,
    };
    if (sg.anonResult) ctx.result = sg.anonResult; // the offers follow the latest Result, the badge never does (X36)
    return ctx;
  };
  // the persistent slot through the shared renderer; hidden when chart-only or renderer absent
  const renderGuidance = () => {
    if (!sgSlot) return;
    const sg = state.sg;
    const SG = sgApi();
    sgSlot.style.minHeight = '';
    clear(sgSlot);
    if (!sg || !sg.resolution || !SG || !SG.render || typeof SG.render.row !== 'function') { sgSlot.hidden = true; return; }
    try {
      SG.render.row(sgSlot, sg.data, sg.resolution, sgRowCtx(sg));
    } catch (error) {
      clear(sgSlot); // a renderer failure never takes the pills down
    }
    sgSlot.hidden = !sgSlot.firstChild;
    sgSlot.dataset.sgFor = String(state.product.id);
  };

  // fit line (design page 01 :231-237): product fit_bias wins over the chart's; nothing
  // without data; the "size up" tip only when a larger size exists on the product
  const sgHasLargerSize = () => {
    const sg = state.sg;
    if (!sg || sg.sizeIdx < 0) return true;
    const values = (state.product.options[sg.sizeIdx] || {}).values || [];
    const R = sg.resolution && sg.resolution.result;
    const cur = state.selections[sg.sizeIdx] || (R && R.size) || null;
    return !cur || values.indexOf(cur) < values.length - 1;
  };
  const renderFit = (data) => {
    const bias = (data.product && data.product.fit_bias) || (data.chart && data.chart.fit_bias) || '';
    if (!/^(true_to_size|runs_small|runs_large)$/.test(bias)) return null;
    const line = document.createElement('div');
    line.className = SG_CLASS_PREFIX + 'fit';
    line.setAttribute('role', 'note');
    line.dataset.fit = bias;
    const bars = document.createElement('span');
    bars.className = SG_CLASS_PREFIX + 'fit-bars';
    bars.setAttribute('aria-hidden', 'true');
    ['small', 'true', 'large'].forEach((k) => {
      const bar = document.createElement('span');
      bar.className = SG_CLASS_PREFIX + 'fit-bar';
      if (bias === 'true_to_size' && k === 'true') bar.classList.add(SG_CLASS_PREFIX + 'fit-bar--on');
      if ((bias === 'runs_small' && k === 'small') || (bias === 'runs_large' && k === 'large')) bar.classList.add(SG_CLASS_PREFIX + 'fit-bar--warn');
      bars.append(bar);
    });
    const label = document.createElement('span');
    label.className = SG_CLASS_PREFIX + 'fit-label';
    label.textContent = sgStr(bias === 'true_to_size' ? 'fit_true' : bias === 'runs_small' ? 'fit_small' : 'fit_large');
    line.append(bars, label);
    const tipKey = bias === 'runs_small' ? 'fit_small_tip' : bias === 'runs_large' ? 'fit_large_tip' : null;
    if (tipKey && (bias !== 'runs_small' || sgHasLargerSize())) {
      const tip = document.createElement('span');
      tip.className = SG_CLASS_PREFIX + 'fit-tip';
      tip.textContent = sgStr(tipKey);
      line.append(tip);
    }
    return line;
  };

  // Phase 2 assets through the base loader (SG.ensure.* reads the dialog's src attributes)
  const ensureSgPart = (kind) => ensureSg().then((sg) => (sg.ensure && typeof sg.ensure[kind] === 'function' ? sg.ensure[kind]() : Promise.reject(new Error('size guide loader unknown'))));
  const ensureSgAll = () => ensureSgPart('finder').then(() => ensureSgPart('rec'));
  let sgCssPromise = null;
  const ensureSgCss = () => {
    if (sgCssPromise) return sgCssPromise;
    const href = modal() && modal().dataset.paexSgCss;
    if (!href) return Promise.reject(new Error('size guide css unknown'));
    sgCssPromise = Promise.all(href.split(',').map((one) => new Promise((resolve, reject) => {   // D09-3: first-paint + drawer sheets
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = one.trim();
      link.onload = () => resolve(true);
      link.onerror = () => {
        sgCssPromise = null; // allow a retry on the next open
        reject(new Error('size guide css failed'));
      };
      document.head.append(link);
    }))).then(() => true);
    return sgCssPromise;
  };

  // the chart was opened (stacked or inline): `chart:` outcome, level, open event
  const sgViewed = () => {
    const sg = state.sg;
    if (!sg) return;
    sg.viewed = true;
    const SG = sgApi();
    if (SG && SG.store) SG.store.bumpLevel('chart', sgGate());
    sgTrack('open', { chart_type: sg.data.chart.type, audience: sg.data.chart.audience, handle: sg.data.chart.handle });
  };
  // the drawer's result reaches the quick-add behind it (= the §1.4 setResult rules on the
  // PLP): a chip pick / save hands the full Resolution (how explicit: the drawer emitted its
  // own manual, a save is no switch → key remembered, never re-emitted; an auto / mru route
  // the drawer resolved itself is ours to emit once, §1.9); an anonymous run / delete hands
  // {status, how:null, profile, result, stale} → re-resolve the real Resolution and keep an
  // anonymous Result for the offers + stamp only (X36). Never a pre-selection here (rule c;
  // NATIVE M4: nothing moves under the open drawer).
  const sgOnDrawerResult = (res) => {
    const sg = state.sg;
    if (!sg || !res) return;
    const prevId = sgProfileId(sg.resolution);
    try {
      if (Array.isArray(res.all)) {
        sg.resolution = res;
        sg.anonResult = null;
        if (res.profile) sg.pickId = res.profile.id;
        sgEmitSwitch(res, res.how === 'explicit');
      } else {
        sg.anonResult = !res.profile && res.result ? res.result : null;
        if (res.profile) sg.pickId = res.profile.id;
        else if (!res.result) sg.pickId = null; // the active profile was deleted → route afresh
        if (sgLogicReady()) { sg.resolution = sgResolve(sg.pickId); sgEmitSwitch(sg.resolution, !!res.profile); } // a save is not a switch (key remembered); a re-route after a delete is
      }
    } catch (error) { return; }
    sgDropPre(prevId);
    sgRerenderPills();
  };
  // render.drawer ctx (§1.4.2)
  const sgDrawerCtx = (sg, mode) => ({
    surface: 'quick_add',
    data: sg.data,
    chart: sg.chart,
    fc: sg.fc,
    sizes: sgSizes(),
    selected: state.selections[sg.sizeIdx],
    resolution: sg.resolution,
    mode,
    idPrefix: SG_ID_PREFIX,
    onChoose: (value, how) => {
      // select THEN hide (§4.4.4: the opener survives the pill re-render)
      sgApply(value, SG_HOW_DRAWER[how] ? how : 'drawer');
      const dialog = document.getElementById(SG_STACKED_ID);
      if (dialog && typeof dialog.hide === 'function') dialog.hide();
    },
    onEvaluated: sgOnDrawerResult,
    track: sgTrack,
  });
  // design 1.15b: a SECOND layer above the quick-add. Build before show() (the trap is
  // snapshotted at show); the engine's opener is always the row toggle (stable node →
  // hide() re-traps the panel through openedBy.closest('[data-trap]')); the close
  // observer hands focus back to the slot control. Any failure ⇒ Phase 1 inline table.
  const openStackedDrawer = (mode, opener, opts) => {
    const dialog = document.getElementById(SG_STACKED_ID);
    const sg = state.sg;
    const fallback = () => { if (typeof sgInlineExpand === 'function') sgInlineExpand(); };
    if (!dialog || typeof dialog.show !== 'function' || !sg) { fallback(); return; }
    const token = state.requestToken;
    const productId = state.product.id;
    const active = document.activeElement;
    const inSlot = !opener && active && sgSlot && sgSlot.contains(active) ? active : null; // Safari never focuses a clicked button → null → focus stays on the toggle after close
    const ret = inSlot && inSlot.closest ? inSlot.closest('[data-paex-sg-return]') : null;
    sgReturn = { el: inSlot, token: ret ? ret.dataset.paexSgReturn || null : null };
    const logicOn = (sg.data.settings || {}).finder !== false; // X28: a finder-off chart never requests the finder / rec assets
    Promise.all([logicOn ? ensureSgAll() : ensureSg(), ensureSgPart('drawer'), ensureSgCss()])
      .then(() => {
        // superseded, or the quick-add closed during the cold load: a drawer never opens without its layer-1 host
        if (token !== state.requestToken || !state.sg || !state.product || state.product.id !== productId || !qaOpen()) { sgReturn = null; return; }
        const SG = sgApi();
        if (!SG.render || typeof SG.render.drawer !== 'function') throw new Error('drawer renderer absent');
        if (logicOn && !state.sg.evaluated && sgLogicReady()) { sgPrepare(state.sg.data); sgRerenderPills(); } // the logic landed with the drawer: resolve + show the row (no pre-selection here)
        const cur = state.sg;
        const key = String(productId) + ':' + mode;
        if (!sgDrawerCtl || dialog.dataset.sgFor !== key) {
          if (sgDrawerCtl) { try { sgDrawerCtl.destroy(); } catch (error) { /* a stale controller never blocks the open */ } }
          sgDrawerCtxObj = sgDrawerCtx(cur, mode);
          sgDrawerCtl = SG.render.drawer(dialog, cur.data, sgDrawerCtxObj);
          dialog.dataset.sgFor = key;
        } else {
          // same product + mode: the controller re-reads sizes / selected from the SAME ctx object (X26: fresh per open)
          sgDrawerCtxObj.sizes = sgSizes();
          sgDrawerCtxObj.selected = state.selections[cur.sizeIdx];
          sgDrawerCtxObj.resolution = cur.resolution;
          sgDrawerCtl.refresh(cur.resolution);
        }
        const toggle = q('.paex-quick-add__sc-toggle');
        if (toggle) toggle.setAttribute('aria-controls', SG_STACKED_ID);
        sgDrawerCtl.open(toggle || opener || null, opts || undefined);
        sgViewed();
      })
      .catch(() => {
        sgReturn = null;
        if (qaOpen()) fallback(); // asset, CSS or renderer refused → Phase 1 inline table, modal unaffected
      });
  };
  // after the stacked drawer closes (panel already re-trapped on the toggle): focus back
  // to the slot control that opened it, or its re-rendered twin by data-paex-sg-return
  const sgAfterStackedClose = () => {
    const r = sgReturn;
    sgReturn = null;
    if (!r) return;
    let el = r.el && r.el.isConnected && sgSlot && sgSlot.contains(r.el) ? r.el : null;
    if (!el && r.token && sgSlot && !sgSlot.hidden) el = sgSlot.querySelector('[data-paex-sg-return="' + CSS.escape(String(r.token)) + '"]');
    if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
  };

  // resetSizeChart + slot emptied + stacked drawer controller destroyed (per product)
  const resetSizeRow = () => {
    resetSizeChart();
    sgInlineExpand = null;
    if (sgSlot) {
      sgSlot.hidden = true;
      sgSlot.style.minHeight = '';
      clear(sgSlot);
      delete sgSlot.dataset.sgFor;
    }
    const stacked = document.getElementById(SG_STACKED_ID);
    if (stacked && stacked.hasAttribute('open') && typeof stacked.hide === 'function') stacked.hide(); // defensive: a load never starts under an open drawer
    if (sgDrawerCtl) {
      try { sgDrawerCtl.destroy(); } catch (error) { /* additive */ }
      sgDrawerCtl = null;
    }
    sgDrawerCtxObj = null;
    if (stacked) delete stacked.dataset.sgFor;
  };

  // Idempotent per product (keyed by product.id): pill clicks never touch
  // this mount, and a repeat render for the same product is a no-op, so
  // the row can never duplicate. Product change (open/swap/back) collapses
  // and rebuilds via the renderLoading reset + this call (from loadProduct,
  // once the chart island has settled; null ⇒ hidden). Mount order: fit line →
  // chart toggle (+ inline region) → SR description span for the badged pill.
  const renderSizeRow = (data) => {
    const mount = q('[data-paex-qa-size-chart]');
    if (!mount) return;
    try {
      const product = state.product;
      if (!product) { resetSizeChart(); return; }
      if (mount.dataset.chartFor === String(product.id)) return;
      resetSizeChart();
      if (!data || !data.chart || !Array.isArray(data.chart.rows) || !data.chart.rows.length) return;
      const fit = renderFit(data);
      if (fit) mount.append(fit);
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'paex-quick-add__sc-toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-haspopup', 'dialog');
      toggle.setAttribute('aria-controls', 'Paex-Quick-Add-Size-Chart');
      const label = document.createElement('span');
      label.className = 'paex-quick-add__sc-label';
      label.id = 'Paex-Quick-Add-Size-Chart-Label'; // names the table caption + scroll region (single mount per modal)
      // per-chart link text from the island (Tableau des tailles / Tailles de
      // ce costume / Mesures…), locale fallback from the mount's data-label
      label.textContent = (data.strings && data.strings.link) || mount.dataset.label || t('sizeChart', 'Size chart', 'Tableau des tailles');
      const chevron = document.createElement('span');
      chevron.className = 'paex-quick-add__sc-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      toggle.append(label, chevron);
      const region = document.createElement('div');
      region.className = 'paex-quick-add__sc-content';
      region.id = 'Paex-Quick-Add-Size-Chart';
      region.hidden = true;
      let built = false;
      // Phase 1 inline expand = the degrade path
      const inlineExpand = () => {
        toggle.setAttribute('aria-controls', 'Paex-Quick-Add-Size-Chart');
        const expand = region.hidden;
        if (expand && !built) {
          built = true;
          region.setAttribute('aria-busy', 'true'); // announced as loading until the table is in
          ensureSg()
            .then((sg) => {
              region.removeAttribute('aria-busy');
              if (mount.dataset.chartFor !== String(product.id)) return; // product swapped meanwhile
              const table = sg.render.table(data, { className: 'paex-quick-add__sc-table', caption: label.textContent, split: true }); // Phase 2.1: body / costume split (one implementation in base)
              const scroller = document.createElement('div');
              scroller.className = 'paex-quick-add__sc-scroll';
              scroller.setAttribute('tabindex', '0'); // keyboard-scrollable region
              scroller.setAttribute('role', 'region'); // a focusable scroller needs a role + a name
              scroller.setAttribute('aria-labelledby', 'Paex-Quick-Add-Size-Chart-Label');
              scroller.append(table);
              region.append(scroller);
              sgViewed();
            })
            .catch(() => {
              built = false; // the next expand retries
              region.removeAttribute('aria-busy');
              // collapse again so a screen reader never hears "expanded" over an empty region
              region.hidden = true;
              toggle.setAttribute('aria-expanded', 'false');
            });
        } else if (expand) {
          sgViewed();
        }
        region.hidden = !expand;
        toggle.setAttribute('aria-expanded', String(expand));
      };
      sgInlineExpand = inlineExpand;
      toggle.addEventListener('click', () => {
        if (state.sg) openStackedDrawer('chart', toggle); // design 1.15b: second layer above the quick-add
        else inlineExpand(); // no size-guide context (switch off mid-session) → Phase 1 inline expand, unchanged
      });
      const desc = document.createElement('span');
      desc.id = SG_ID_PREFIX + 'Desc';
      desc.className = 'visually-hidden';
      mount.append(toggle, region, desc);
      mount.dataset.chartFor = String(product.id);
      mount.hidden = false;
      sgUpdateDesc();
    } catch (error) {
      resetSizeChart(); // any failure ⇒ hidden mount, modal unaffected
    }
  };

  const renderProduct = () => {
    const product = state.product;
    q('[data-paex-qa-title]').textContent = product.title;
    // "See full details" (design full-page sheet: right of the title,
    // 12/600) — a real anchor to the product PDP, normal navigation.
    const details = q('[data-paex-qa-details]');
    if (details) {
      details.href = state.productPath;
      details.hidden = false;
    }
    const media = q('[data-paex-qa-media]');
    clear(media);
    (product.images || []).slice(0, 4).forEach((src) => {
      const img = document.createElement('img');
      img.src = imageUrl(src, 600);
      img.alt = '';
      img.loading = 'lazy';
      media.append(img);
    });
    if (!media.firstChild) {
      const ph = document.createElement('span');
      ph.className = 'paex-quick-add__media-ph';
      media.append(ph);
    }
    renderServices(); // BOPIS: the service rows + selection first (pills and CTA read them)
    renderOptions();
    renderPrice();
    renderRows();
    renderCta();
    // Size chart row: rendered from loadProduct once the chart island has
    // settled (covers initial open, swapTo AND goBack the same way).
  };

  /* ---------------- BOPIS: "Buy online" selector in the modal (2026-10-22, lane E) ----
   * Mirrors the PDP block's renderServices (assets/paex-delivery-method.js) on the modal's
   * palette: rows ship / pickup / local, state vocabulary in | only | some | top | out |
   * paused | prompt, rendered ONLY when window.paexFulfil says the resolved store
   * participates and the shopper is inside its pickup radius (participates().gated). The
   * selection follows seams §3.3: cart mode → the shopper's pick in this modal (or the page
   * selection paexFulfil.select carried) → by stock (ship when the DC has it, else pickup).
   * Stock = paexFulfil.stock(handle) (?view=paex-stock, sessionStorage 'paex:stock'),
   * fetched once per open; until it lands the rows read the product payload (ship) and
   * nothing is struck (fail open — the gate re-checks on add). Texts: paexFulfil.t (the
   * paex.fulfil.* island) + rccStrings for the tier words; never a literal.
   * ------------------------------------------------------------------- */
  const fulfil = () => (window.paexFulfil && typeof window.paexFulfil === 'object' ? window.paexFulfil : null);
  const ft = (key, vars) => {
    const api = fulfil();
    if (!api || typeof api.t !== 'function') return '';
    let out = '';
    try { out = String(api.t(key, vars || {}) || ''); } catch (error) { out = ''; }
    return /^Translation missing/i.test(out) ? '' : out;
  };
  const fulfilShown = () => { const box = q('[data-paex-qa-services]'); return !!(box && !box.hidden); };
  // export 4 item 6: low stock = 3 or fewer — the selector's own threshold (texts island
  // settings.tierLowSelector, P5), 3 until it lands; never the PLP tier (paex_tier_low)
  const fulfilTierLow = () => { const api = fulfil(); return Number(api && api.settings && api.settings.tierLowSelector) || 3; };
  const fulfilTierTop = () => { const api = fulfil(); return Number(api && api.settings && api.settings.tierTop) || 10; };
  // the participation record when the selector applies to this visitor, else null (today's modal)
  // memoized for ~100ms: one render pass asks it once per pill / row (participates() re-reads
  // the store islands); a store or mode change always lands in a later pass
  let fulfilDecisionCache = { t: 0, v: null };
  const fulfilDecision = () => {
    const now = Date.now();
    if (now - fulfilDecisionCache.t < 100) return fulfilDecisionCache.v;
    const api = fulfil();
    let part = null;
    if (api && typeof api.participates === 'function' && ft('legend')) {
      try { part = api.participates(api.storeId()); } catch (error) { part = null; }
    }
    fulfilDecisionCache = { t: now, v: part && part.gated && part.store ? part : null };
    return fulfilDecisionCache.v;
  };
  // the stock map landed with entries ({} = the view answered nothing: unknown, fail open)
  const fulfilStockKnown = () => !!(state.fulfil.stock && Object.keys(state.fulfil.stock).length);
  // the store the rows name: the cart's store once a mode exists (6c), else the resolved store
  const fulfilStoreId = (part) => {
    const api = fulfil();
    try {
      const mode = api && typeof api.mode === 'function' ? api.mode() : null;
      const loc = mode && typeof api.locationId === 'function' ? api.locationId(mode) : null;
      if (loc && api.store(loc)) return String(loc);
    } catch (error) { /* resolved store below */ }
    return part ? String(part.storeId) : null;
  };
  const fulfilCartService = () => {
    const api = fulfil();
    try { return api && typeof api.service === 'function' ? api.service() : null; } catch (error) { return null; }
  };
  // the one stock entry for a variant from the ?view=paex-stock map (null until it lands)
  const fulfilEntry = (variantId) => {
    const map = state.fulfil.stock;
    return map && map[String(variantId)] ? map[String(variantId)] : null;
  };
  const fulfilDcQty = (variant) => {
    const entry = fulfilEntry(variant.id);
    if (!entry || entry.dc == null) return null; // unknown: Shopify sellability answers
    return Math.max(0, Number(entry.dc) || 0);
  };
  const fulfilStoreQty = (variant, storeId) => {
    const entry = fulfilEntry(variant.id);
    if (!entry || !entry.stores || entry.stores[String(storeId)] == null) return entry ? 0 : null; // absent entry = 0 once the map landed
    return Math.max(0, Number(entry.stores[String(storeId)]) || 0);
  };
  const fulfilTierFor = (qty) => (qty <= 0 ? 'out' : qty <= fulfilTierLow() ? 'only' : qty >= fulfilTierTop() ? 'top' : 'some');
  // the count word of the Pick up row (export 4 selector.js: "Only 3" at or under the low tier,
  // "7 in stock" above it — qa_tier_only / qa_tier_some, the rcc words only when a key is blank)
  const fulfilTierText = (qty) => (qty > 0 && qty <= fulfilTierLow()
    ? ft('qa_tier_only', { n: qty }) || t('onlyCount', 'Only {count}', 'Plus que {count}').replace('{count}', String(qty))
    : ft('qa_tier_some', { n: qty }) || t('inStock', 'In stock', 'En stock'));
  // candidates for the product-level answer (no size chosen): every variant; else the one
  const fulfilVariants = () => {
    const variant = currentVariant();
    return variant ? [variant] : (state.product ? state.product.variants : []);
  };
  // ship truth = the DC entry when known, else Shopify sellability (the quick-add's own rule)
  const fulfilShipOk = (variant) => {
    const dc = fulfilDcQty(variant);
    return dc == null ? !!variant.available : dc > 0;
  };
  const fulfilLocal = (store) => {
    const api = fulfil();
    if (!api || typeof api.localLine !== 'function' || !store) return null;
    try { return api.localLine(store, typeof api.knownPostal === 'function' ? api.knownPostal() : null, new Date()); } catch (error) { return null; }
  };
  const fulfilPaused = (store, service) => {
    const api = fulfil();
    try { return !!(api && typeof api.pausedToday === 'function' && api.pausedToday(store, service)); } catch (error) { return false; }
  };
  // true when the SELECTED service can fulfil this variant (unknown stock = true: the gate re-checks)
  const fulfilAvailable = (service, variant) => {
    if (!variant || !service) return true;
    if (service === 'ship') return fulfilShipOk(variant);
    const part = fulfilDecision();
    if (!part) return true;
    const storeId = fulfilStoreId(part);
    const store = fulfil().store(storeId);
    if (!store || store[service] !== true || fulfilPaused(store, service)) return false;
    if (service === 'local') { const line = fulfilLocal(store); if (!line || !line.ok) return false; }
    const qty = fulfilStoreQty(variant, storeId);
    return qty == null ? true : qty > 0;
  };
  // 'ship' | 'pickup' | 'local' once the rows are shown, else null (today's modal: no override anywhere)
  const fulfilService = () => (fulfilShown() ? state.fulfil.selected || 'ship' : null);
  // the mode the add asks the gate for (opts.mode): only while the rows are shown
  const fulfilMode = () => {
    const service = fulfilService();
    const api = fulfil();
    if (!service || !api || typeof api.makeMode !== 'function') return null;
    return api.makeMode(service, service === 'ship' ? null : fulfilStoreId(fulfilDecision()));
  };
  const fulfilShortName = (store) => {
    const name = (store && store.name) || '';
    return typeof paexDeliveryShortName === 'function' ? paexDeliveryShortName(name) : name;
  };
  // C5 contract: pauseText(store, svc) = the short paused / blackout sentence; the paused_* keys
  // (P5) answer until it lands
  const fulfilPauseText = (store, service, name) => {
    const api = fulfil();
    let text = '';
    if (api && typeof api.pauseText === 'function') {
      try { text = String(api.pauseText(store, service) || ''); } catch (error) { text = ''; }
    }
    return text || ft(service === 'pickup' ? 'paused_pickup' : 'paused_local', { store: name });
  };
  // "Arrives Fri, Oct 23" for the Ship title line (export 4 item 9 / 5b): the cart drawer's own
  // base estimate (paexFulfil.ui.shipDate — one date per cart, no second engine) through the
  // shared "Arrives {date}" sentence; '' when there is no estimate → the title stays bare
  const fulfilShipArrives = () => {
    const api = fulfil();
    if (!api || !api.ui || typeof api.ui.shipDate !== 'function') return '';
    let date = '';
    try { date = String(api.ui.shipDate() || ''); } catch (error) { date = ''; }
    const sentence = api.shared && api.shared.arrives ? String(api.shared.arrives) : '';
    return date && sentence ? sentence.replace('{date}', date) : '';
  };
  // the status line with its count at 600 (export 4 item 6: counts weight 600, the sentence 500
  // ink): "7 in stock at Québec" → text · <b>7 in stock</b> · text. textContent stays the plain
  // sentence, so a re-render with the same words is a no-op.
  const setServiceStatus = (row, text, count) => {
    const target = row.querySelector('[data-service-status]');
    if (!target) return;
    const idx = count ? text.indexOf(count) : -1;
    const hasCount = !!target.querySelector('.paex-quick-add__service-count');
    if (target.textContent === text && hasCount === idx >= 0) return;
    clear(target);
    if (idx < 0) { target.textContent = text; return; }
    const strong = document.createElement('b');
    strong.className = 'paex-quick-add__service-count';
    strong.textContent = count;
    target.append(text.slice(0, idx), strong, text.slice(idx + count.length));
  };
  // the CTA for the selected service: { text, disabled, service } or null (today's label)
  const fulfilCtaSpec = (variant) => {
    const service = fulfilService();
    if (!service || !variant) return null;
    const part = fulfilDecision();
    const storeId = part ? fulfilStoreId(part) : null;
    const store = storeId ? fulfil().store(storeId) : null;
    const label = state.product.variants.length > 1 ? state.selections.filter(Boolean).join(' / ') : '';
    // export 4 button(): "Add to cart" / "Add for pickup" / "Add for same-day delivery", and ONE
    // disabled label "Not available" (cta_unavailable, P5 — the row above and the struck pill
    // carry the reason). Until that key lands the button names the reason itself (round 2 n=45).
    const unavailable = ft('cta_unavailable');
    const shared = (fulfil() && fulfil().shared) || {};
    if (service === 'ship') {
      if (fulfilShipOk(variant)) {
        const add = shared.add_to_cart ? String(shared.add_to_cart) : '';
        return add ? { text: add, disabled: false, service } : null;
      }
      const text = unavailable || (label && ft('unavailable_ship_variant', { variant: label })) || ft('ship_store_only') || ft('ship_out');
      return { text, disabled: true, service };
    }
    if (fulfilAvailable(service, variant)) {
      const text = service === 'pickup' ? ft('add_pickup', { store: fulfilShortName(store) }) : ft('add_local');
      return text ? { text, disabled: false, service } : null;
    }
    // a missing postal code is the one exception (design 1.6 / S.6: the button keeps asking for
    // the code): local_postal_missing. Reason fallbacks: a pause or a postal outside the zone is
    // size-less; only a store-0 variant blames the size.
    let text = '';
    const storeOut = (() => {
      if (!store) return false;
      const qty = fulfilStoreQty(variant, storeId);
      return qty != null && qty <= 0;
    })();
    const paused = !!(store && fulfilPaused(store, service));
    const line = !paused && service === 'local' && store && !storeOut ? fulfilLocal(store) : null;
    if (paused) {
      text = unavailable || ft(service === 'pickup' ? 'card_pickup_out' : 'card_local_out');
    } else if (line && line.reason === 'no_postal') {
      text = ft('local_postal_missing');
    } else if (unavailable) {
      text = unavailable;
    } else if (line && line.reason === 'outside_zone') {
      text = ft('card_local_out');
    }
    if (!text) text = label ? ft(service === 'pickup' ? 'unavailable_pickup_variant' : 'unavailable_local_variant', { variant: label }) : '';
    // one-size item: a store-0 line reads "Not in stock at {store}" for BOTH services, the same
    // sentence as the local row above it (round 2 review); only a missing postal says so
    if (!text) text = service === 'pickup' || storeOut ? ft('pickup_out', { store: fulfilShortName(store) }) : ft('local_postal_missing');
    return { text, disabled: true, service };
  };
  // the strike reason for a size pill: every matching variant unavailable for the selected service
  const fulfilPillReason = (optionIndex, value) => {
    const service = fulfilService();
    if (!service || !fulfilStockKnown()) return '';
    const candidates = state.product.variants.filter((variant) =>
      variant['option' + (optionIndex + 1)] === value && matchesOtherSelections(variant, optionIndex));
    if (!candidates.length || candidates.some((variant) => fulfilAvailable(service, variant))) return '';
    return ft('unavailable_' + service + '_variant', { variant: value });
  };
  // fetch the product's stock map once per open; the rows / pills / CTA re-read it when it lands
  const fulfilLoadStock = (product, token) => {
    const api = fulfil();
    if (!api || typeof api.stock !== 'function' || !product || !product.handle) return;
    if (state.fulfil.stockFor === product.handle && state.fulfil.stock) return;
    api.stock(product.handle).then((map) => {
      if (token !== state.requestToken || !state.product || state.product.handle !== product.handle) return;
      state.fulfil.stock = map && typeof map === 'object' ? map : {};
      state.fulfil.stockFor = product.handle;
      renderServices();
      renderOptions();
      renderRows();
      renderCta();
      applyRecFilter();
    }).catch(() => { /* fail open: nothing struck, the gate re-checks */ });
  };
  const renderServices = () => {
    const box = q('[data-paex-qa-services]');
    if (!box || !state.product) return;
    const api = fulfil();
    const part = fulfilDecision();
    if (!part) {
      if (!box.hidden) box.hidden = true;
      state.fulfil.selected = null;
      return;
    }
    const storeId = fulfilStoreId(part);
    const store = api.store(storeId) || part.store;
    const name = fulfilShortName(store) || store.name || ''; // the rows name the store like the cards and the sticky bar
    const legend = box.querySelector('[data-paex-qa-services-legend]');
    if (legend && legend.textContent !== ft('legend')) legend.textContent = ft('legend');
    const variants = fulfilVariants();
    const variant = currentVariant();
    const shipOk = variants.some(fulfilShipOk);
    const storeQty = variants.reduce((best, v) => {
      const qty = fulfilStoreQty(v, storeId);
      return Math.max(best, qty == null ? (fulfilStockKnown() ? 0 : -1) : qty);
    }, -1);
    const stockKnown = storeQty >= 0; // -1 = the map has not landed
    const pickupShown = store.pickup === true;
    const localShown = store.local === true;
    const pickupPaused = pickupShown && fulfilPaused(store, 'pickup');
    const localPaused = localShown && fulfilPaused(store, 'local');
    const pickupOk = pickupShown && !pickupPaused && (stockKnown ? storeQty > 0 : true);
    const local = localShown ? fulfilLocal(store) : null;
    const localOk = !!local && !localPaused && local.ok && (stockKnown ? storeQty > 0 : true);
    const shown = { ship: true, pickup: pickupShown, local: localShown };

    // selection: the modal's own pick (a row the shopper tapped since the modal opened; reset on
    // open and whenever the cart mode moves) → cart mode → the page selection → by stock.
    // Visual review 2026-10-23: with the cart mode first, a ship cart pinned the Ship row and a
    // tap on Pick up was reverted by this re-render, so the add never reached the interception.
    let selected = null;
    const cartService = fulfilCartService();
    if (state.fulfil.chosen && state.fulfil.selected && shown[state.fulfil.selected]) selected = state.fulfil.selected;
    if (!selected && cartService && shown[cartService]) selected = cartService;
    if (!selected && !cartService && typeof api.selection === 'function') {
      try { const sel = api.selection(); if (sel && shown[sel.service]) selected = sel.service; } catch (error) { selected = null; }
    }
    if (!selected) selected = shipOk ? 'ship' : (pickupOk && stockKnown ? 'pickup' : 'ship');
    state.fulfil.selected = selected;

    const pauseNote = String(store.pauseNote || '').trim();
    const tierState = stockKnown ? fulfilTierFor(storeQty) : 'in';
    const tierWord = stockKnown ? fulfilTierText(storeQty) : t('inStock', 'In stock', 'En stock');
    const pickupPausedText = pickupPaused ? fulfilPauseText(store, 'pickup', name) : '';
    const localPausedText = localPaused ? fulfilPauseText(store, 'local', name) : '';
    const eligibleText = ft('local_eligible', { store: name }); // P5 contract "Eligible · from {store}"; '' until it lands
    // The rows mirror the PDP rows (export 4 items 5–9, 5b.1 / 5b.2): Ship = title line with the
    // date, status "In stock online" / "Sold out online" (never a count); Pick up = "{n} in stock
    // at {store}" / "Only {n} at {store}" / "Not in stock at {store}"; Same-day = "Eligible · from
    // {store}" / outside / the postal prompt. No fee anywhere (item 14).
    const rows = {
      ship: {
        state: shipOk ? 'in' : 'out',
        label: ft('service_ship'),
        date: shipOk ? fulfilShipArrives() : '',
        status: shipOk ? ft('ship_in') : ft('ship_out'),
        detail: '',
        reason: '',
        ok: shipOk,
      },
      pickup: {
        state: pickupPaused ? 'paused' : (pickupOk ? tierState : 'out'),
        label: ft('service_pickup'),
        status: pickupPaused
          ? pickupPausedText
          : (pickupOk ? ft('pickup_in', { tier: tierWord, store: name }) : ft('pickup_out', { store: name })),
        count: pickupOk && !pickupPaused && stockKnown ? tierWord : '',
        detail: '',
        reason: pickupPaused ? pauseNote : '',
        ok: pickupOk,
      },
      // design 1.5 / 1.11 (round 2, n=1/44): paused → store stock 0 → postal prompt → outside
      // the zone → tier, the same precedence as the PDP row — a store-0 item says "Not in
      // stock at {store}" and never asks for a postal code first
      local: {
        state: localPaused
          ? 'paused'
          : (stockKnown && storeQty <= 0
            ? 'out'
            : (!local || local.reason === 'no_postal' ? 'prompt' : (local.ok ? tierState : 'out'))),
        label: ft('service_local'),
        status: localPaused
          ? localPausedText
          : (stockKnown && storeQty <= 0
            ? ft('pickup_out', { store: name })
            : (!local || local.reason === 'no_postal' ? ft('local_prompt') : (local.ok ? (eligibleText || local.text || '') : (local.text || '')))),
        detail: '',
        reason: localPaused ? pauseNote : '',
        // no inline editor here: the prompt state is read, not chosen (the PDP row owns it) and
        // it only shows while the store has the item (stock 0 is 'out' above)
        ok: localOk,
      },
    };
    // Pick up row detail = the ONE ready sentence (readyLine().text, lane D3 contract: "Usually ready
    // in 2 hours · held 3 days" / closed "Ready tomorrow around noon (…) · held 3 days"), shown under
    // the status line ONLY while the Pick up row is selected (design 5b.2); never when + base.
    if (selected === 'pickup' && pickupOk && typeof api.readyLine === 'function') {
      try { const line = api.readyLine(store, new Date()); rows.pickup.detail = (line && line.text) || ''; } catch (error) { rows.pickup.detail = ''; }
    }
    // Same-day row detail = localLine().text (C5 contract: "Order before 6 p.m. for today" / after the
    // cutoff "Delivered tomorrow around noon"), under the selected row only — never the fee (export 4
    // item 14: the fee shows in the order summary and at checkout only). Until local_eligible lands
    // the status line already reads localLine().text, so the detail stays empty rather than repeat it.
    if (selected === 'local' && localOk && local && local.text && rows.local.status !== local.text) rows.local.detail = local.text;

    box.querySelectorAll('[data-service]').forEach((row) => {
      const service = row.dataset.service;
      const spec = rows[service];
      if (!spec) return;
      const isShown = !!shown[service];
      if (row.hidden !== !isShown) row.hidden = !isShown;
      if (!isShown) return;
      const isSelected = service === selected;
      if (row.dataset.state !== spec.state) row.dataset.state = spec.state;
      row.toggleAttribute('data-selected', isSelected);
      const radio = row.querySelector('[data-service-radio]');
      if (radio) {
        if (radio.checked !== isSelected) radio.checked = isSelected;
        if (spec.ok) radio.removeAttribute('aria-disabled');
        else radio.setAttribute('aria-disabled', 'true');
      }
      const setText = (selector, value) => {
        const target = row.querySelector(selector);
        if (target && target.textContent !== value) target.textContent = value;
        return target;
      };
      setText('[data-service-label]', spec.label);
      const date = setText('[data-service-date]', spec.date ? ' · ' + spec.date : '');
      if (date && date.hidden !== !spec.date) date.hidden = !spec.date;
      setServiceStatus(row, spec.status, spec.count || '');
      setText('[data-service-detail]', spec.detail);
      const reason = setText('[data-service-reason]', spec.reason);
      if (reason) reason.hidden = !spec.reason;
    });

    // "Only {n} available for pickup": the store cap on a one-unit add, at or under the low tier
    const qtyLine = q('[data-paex-qa-fulfil-qty]');
    if (qtyLine) {
      const cap = variant && (selected === 'pickup' || selected === 'local') ? fulfilStoreQty(variant, storeId) : null;
      const capText = cap != null && cap > 0 && cap <= fulfilTierLow() ? ft(selected === 'pickup' ? 'qty_cap_pickup' : 'qty_cap_local', { n: cap }) : '';
      if (qtyLine.textContent !== capText) qtyLine.textContent = capText;
      qtyLine.hidden = !capText;
    }
    if (box.hidden) box.hidden = false;
  };
  // a row tapped: page-local choice (paexFulfil.select → paex:fulfil-select, no write); an
  // unavailable row cannot be chosen (its reason is still read) — the current choice stands
  const onServiceChange = (event) => {
    const box = q('[data-paex-qa-services]');
    const radio = event.target.closest && event.target.closest('[data-service-radio]');
    if (!box || !radio || !box.contains(radio)) return;
    const service = radio.value;
    if (radio.getAttribute('aria-disabled') === 'true') {
      const current = state.fulfil.selected;
      const back = current && box.querySelector(`[data-service-radio][value="${current}"]`);
      if (back) back.checked = true;
      return;
    }
    state.fulfil.selected = service;
    state.fulfil.chosen = true;
    const api = fulfil();
    if (api && typeof api.select === 'function') {
      try { api.select(service, service === 'ship' ? null : fulfilStoreId(fulfilDecision())); } catch (error) { /* the modal choice still applies */ }
    }
    renderServices();
    renderOptions();
    renderRows();
    renderCta();
    applyRecFilter();
  };
  // the cart mode moved (set / cleared / boot): the carrier wins the selection again (seams §3.3),
  // the recs re-render for the new service (the section filters on the cart attribute)
  const onFulfilChange = () => {
    recsCache.clear();
    recsSettled.clear();
    if (!qaOpen() || !state.product) return;
    state.fulfil.chosen = false;
    state.fulfil.selected = null;
    renderServices();
    renderOptions();
    renderRows();
    renderCta();
    const wrap = q('[data-paex-qa-recs]');
    if (wrap) delete wrap.dataset.renderedFor;
    const token = state.requestToken;
    recsFor(state.productPath).then((payload) => {
      if (token !== state.requestToken) return;
      renderRecs(payload);
    });
  };
  // a rec item for the EFFECTIVE service (cart mode → the modal's row → none = visible):
  // ship needs the DC entry ≠ 0 (absent = unknown = visible), pickup / same-day the store entry > 0
  const recVisible = (item) => {
    const api = fulfil();
    if (!api || !item || !item.fs) return true;
    const cartService = fulfilCartService();
    let service = cartService;
    let loc = null;
    try { loc = cartService ? api.locationId() : null; } catch (error) { loc = null; }
    if (!service) {
      service = fulfilService();
      if (!service) return true;
      loc = service === 'ship' ? null : fulfilStoreId(fulfilDecision());
    }
    if (service === 'ship') return !(item.fs.dc != null && Number(item.fs.dc) <= 0);
    if (!loc) return true;
    const qty = item.fs.stores && item.fs.stores[String(loc)];
    return Number(qty) > 0;
  };
  const applyRecFilter = () => {
    const wrap = q('[data-paex-qa-recs]');
    if (!wrap || wrap.dataset.renderedFor !== state.productPath) return;
    const cards = Array.from(wrap.querySelectorAll('.paex-quick-add__rec[data-rec-id]'));
    if (!cards.length) return;
    let visible = 0;
    cards.forEach((card) => {
      const item = recItems.get(card.dataset.recId);
      const show = recVisible(item);
      card.toggleAttribute('data-fulfil-hidden', !show);
      if (show) visible += 1;
    });
    wrap.hidden = visible === 0; // empty slot → no frame
  };
  const recItems = new Map(); // rec id → payload item of the rendered list (for the filter)
  document.addEventListener('paex:fulfil-change', () => { try { onFulfilChange(); } catch (error) { /* additive */ } });
  document.addEventListener('paex:fulfil-intercept', (event) => {
    const d = event && event.detail;
    if (d && d.opts && d.opts.fulfilSource === 'quick-add') state.fulfil.intercepted = true;
  });

  /* ---------------- cart add (mirrors the nimstrata bridge) ---------------- */

  // W3.1 (2026-08-01): SectionDynamicUpdate.updateSections replaces
  // #Cart-Drawer-Details — the subtree webvista.trapFocus bound its Tab
  // handling to — so after the quick-add→drawer handoff the trap pointed
  // at detached nodes: aria-modal leaked (13/25 tab stops escaped the
  // open drawer) and ESC died once focus wandered out. Re-trap while the
  // drawer is open, keeping the active element when it still lives inside
  // the trap. M5 (2026-08-03): the shared re-arm (and the W3 typeof-gate
  // lore) lives in paex-cart-bridge.js; no-op when the bridge is absent,
  // same degrade as the old typeof-gates.
  const retrapDrawer = (drawer) => {
    const b = window.PaexCartBridge;
    if (b && drawer) b.retrapDrawer(drawer);
  };

  // Silent half of the cart contract: bubble + drawer section refetch and the
  // theme cartUpdate publish, WITHOUT drawer.show. Returns the drawer element
  // (or null) so the CTA path can open it; recommendation adds never do.
  const refreshCartSections = async (cartData) => {
    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return null;
    if (drawer.hasAttribute('data-status-silence')) return null;

    // M5 (2026-08-03): shared descriptor list (drawer entry skipped when the
    // section id stamp is missing, as before — bridge conditions the push).
    const sections = window.PaexCartBridge
      ? window.PaexCartBridge.sectionsToRender(drawer)
      : [{ id: 'Cart-Icon-Bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
    try {
      const names = sections.map((entry) => entry.section).join(',');
      // M6 (2026-08-27): no-store + buster — Shopify sends no Cache-Control
      // on ?sections=, so the plain same-URL GET could replay a stale body.
      const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(names)}&_=${Date.now()}`, { cache: 'no-store' });
      if (response.ok) {
        const payload = await response.json();
        if (typeof SectionDynamicUpdate !== 'undefined') {
          SectionDynamicUpdate.updateSections(sections, payload);
          retrapDrawer(drawer); // W3.1: repaint detached the trapped nodes
        }
      }
    } catch (error) { /* drawer still opens; content refreshes on next mutation */ }

    try {
      // M5 (2026-08-03): publish + its typeof-gate lore (W3 audit) live in
      // the bridge now; a throwing subscriber still never blocks the drawer.
      if (window.PaexCartBridge) window.PaexCartBridge.publishCart('paex-quick-add', cartData);
    } catch (error) { /* subscribers refresh on next mutation */ }

    return drawer;
  };

  const refreshCartAndOpenDrawer = async (opener, cartData) => {
    // Perceived speed (2026-07-31, same reorder as the nimstrata bridge):
    // show the drawer immediately; the section fetch swaps its contents
    // in place when it lands.
    const drawer = document.getElementById('Cart-Drawer');
    if (drawer && !drawer.hasAttribute('data-status-silence') && typeof drawer.show === 'function') {
      drawer.show(opener);
    }
    await refreshCartSections(cartData);
  };

  // readable sentence — never a measurement or a name (§1.8 / Law 25); the ONE encoder is
  // assets/paex-size-advice.js over the base asset's outcomeParts, both injected on the charted
  // open (loadProduct → ensureSg) — the bounded wait is the backstop; null ⇒ the cart backstop stamps
  const sgAdvice = async () => {
    try {
      const sg = state.sg;
      if (!sg) return null;
      const added = sg.sizeIdx < 0 ? null : state.selections[sg.sizeIdx];
      if (!sgApi() || !window.paexSizeAdvice) {
        await Promise.race([Promise.all([ensureSg().catch(() => null), ensureSgAdvice()]), new Promise((resolve) => setTimeout(resolve, 1500))]);
      }
      const SG = sgApi();
      if (!SG || !SG.track || typeof SG.track.advice !== 'function') return null;
      const res = sg.resolution;
      return SG.track.advice(SG.track.outcomeParts({
        result: sgCurrentResult(),
        resolution: res || null,
        added,
        chartOpened: sg.viewed,
        src: 'qa',
        chart: sg.data.chart.handle,
        sel: state.sgPre ? 'pre' : (state.touched ? 'tap' : ''),
      }));
    } catch (error) {
      return null;
    }
  };

  const addToCart = async () => {
    const variant = currentVariant();
    if (!variant || !variant.available || state.busy) return;
    state.busy = true;
    const cta = q('[data-paex-qa-cta]');
    cta.disabled = true;
    cta.classList.add('paex-quick-add__cta--busy'); // in-flight spinner (parity with the card circle)
    cta.setAttribute('aria-busy', 'true');
    let instantArmed = false;
    try {
      // M6 (2026-08-27, cart-drawer fix batch): sections ride the add POST
      // itself through the bridge (queued + token-gated paint) — ONE round
      // trip instead of add + serial ?sections= GET, so the drawer opens
      // already painted and a stale/empty state can never read as final.
      // Localized route included (the old literal '/cart/add.js' skipped the
      // /fr prefix). Errors keep the modal open with the inline row, as before.
      const bridge = window.PaexCartBridge;
      const viaBridge = !!(bridge && typeof bridge.mutate === 'function');
      // Phase 2: ONE payload for both paths; the readable advice rides as the hidden line
      // property for charted products only (key omitted otherwise — uncharted adds unchanged)
      const payload = { id: variant.id, quantity: 1 };
      const sgText = await sgAdvice();
      if (sgText) payload.properties = { '_Size advice': sgText };
      let cartData = null;
      // Instant open (2026-10-10, Sasha: no instant open from the quick add): with the bridge in
      // charge, close the modal and open the drawer under its loading veil BEFORE the add round
      // trip (Shopify's cart mutation alone is 0.4-0.8 s on this store); the bridge paints the
      // sections into the open drawer and its cartUpdate publish lifts the veil
      // (assets/paex-cart-instant.js). Errors then surface as the theme toast instead of the
      // in-modal row (the modal is already closed). Same scroll-lock handoff as the success path.
      const instant = window.PaexCartInstant;
      if (viaBridge && instant && typeof instant.arm === 'function') {
        const opener = state.opener;
        modal().hide();
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        instantArmed = instant.arm(opener);
      }
      if (viaBridge) {
        // BOPIS: the bridge runs paexFulfil.gate before the POST — handle + the stock map let
        // check() read ?view=paex-stock without the PDP embed; mode = the modal's row while the
        // selector is shown (a row other than the cart's → the interception). Blocked → the
        // bridge resolves null and lifts the veil; the interception UI (or its toast backstop)
        // owns the message, so no add-error toast here.
        state.fulfil.intercepted = false;
        const addOpts = { source: 'paex-quick-add', fulfilSource: 'quick-add', handle: state.product.handle || '' };
        if (state.fulfil.stock) addOpts.stock = state.fulfil.stock;
        const mode = fulfilMode();
        if (mode) addOpts.mode = mode;
        const resp = await bridge.mutate('cart/add.js', payload, addOpts);
        if (!resp && state.fulfil.intercepted) { state.fulfil.intercepted = false; return; }
        if (!resp || resp.status) throw new Error((resp && (resp.description || resp.message)) || 'add failed');
        cartData = resp;
      } else {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('add failed');
        cartData = await response.json().catch(() => null);
      }
      // Phase 2: order attribute "_Size guide" after the add (TRACKING §c / §j; shares
      // MEM.attrWritten with the base's cartUpdate subscriber — never a double POST)
      try {
        const SG = sgApi();
        if (SG && SG.track) SG.track.levelAttribute();
      } catch (error) { /* additive */ }
      if (instantArmed) return; // drawer already open + painted by the bridge; veil lifted on cartUpdate
      const opener = state.opener;
      modal().hide();
      // SCROLL-LOCK HANDOFF (merchant 2026-07-31: closing the drawer after
      // a modal add jumped to the top). Ascent's enablePageScroll (fired
      // by modal.hide) restores the page position inside a
      // requestAnimationFrame; if drawer.show re-locks before that frame,
      // disablePageScroll reads scrollY=0 and the drawer's close later
      // "restores" to the top. Two rAFs let the modal's restore land
      // first, so the drawer saves the true PLP offset.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (viaBridge) {
        // bridge already painted the drawer + bubble and published cartUpdate —
        // just open it (silence-gated, same as refreshCartAndOpenDrawer did)
        const drawer = document.getElementById('Cart-Drawer');
        if (drawer && !drawer.hasAttribute('data-status-silence') && typeof drawer.show === 'function') {
          drawer.show(opener);
        }
      } else {
        await refreshCartAndOpenDrawer(opener, cartData);
      }
    } catch (error) {
      if (instantArmed) {
        // modal already closed: lift the veil (drawer stays on the unchanged cart) and toast
        try { window.PaexCartInstant.lift(false); } catch (e) { /* additive */ }
        const msg = t('addError', 'Could not add to cart. Please try again.', "Impossible d'ajouter au panier. Réessayez.");
        if (typeof webvista !== 'undefined' && typeof webvista.popToast === 'function') webvista.popToast(msg, 'error');
      } else {
        const rows = q('[data-paex-qa-rows]');
        clear(rows);
        rows.append(row('a', t('addError', 'Could not add to cart. Please try again.', "Impossible d'ajouter au panier. Réessayez.")));
        renderCta();
      }
    } finally {
      state.busy = false;
      cta.classList.remove('paex-quick-add__cta--busy');
      cta.removeAttribute('aria-busy');
    }
  };

  /* ---------------- recommendations (PDP 'Complete the costume' mirror) ----
   * Source: the paex-quick-add-recs section via the Section Rendering API —
   * the exact PDP contract (S&D related-products metafield only, no
   * algorithmic fallback, sold-out/add-on/same-type excluded, cap 4). The
   * payload is JSON between sentinel markers, extracted from the raw
   * response text and JSON.parsed — never DOM-parsed. Fetch happens AFTER
   * the core modal content renders; empty list keeps the container hidden
   * (no gap). Variant-less recs single-tap add through the same cart
   * contract but silently: the modal stays open and the drawer does NOT
   * pop (refreshCartSections only). Sized recs navigate to their PDP
   * (single-tap rule from the add-on slot / cart cross-sell).
   * ------------------------------------------------------------------- */

  const recsFor = (productPath) => {
    if (recsCache.has(productPath)) return recsCache.get(productPath);
    const promise = fetch(`${productPath}?section_id=paex-quick-add-recs`)
      .then((response) => {
        if (!response.ok) throw new Error('recs fetch failed');
        return response.text();
      })
      .then((html) => {
        const start = html.indexOf(RECS_OPEN);
        const end = html.indexOf(RECS_CLOSE);
        if (start === -1 || end === -1 || end <= start) return null;
        return JSON.parse(html.slice(start + RECS_OPEN.length, end));
      })
      .then((payload) => {
        recsSettled.set(productPath, payload); // fix 21: known from now on
        return payload;
      })
      .catch(() => {
        recsCache.delete(productPath); // allow a retry on the next open
        recsSettled.delete(productPath); // errors stay UNKNOWN, not empty
        return null;
      });
    recsCache.set(productPath, promise);
    return promise;
  };

  const showRecError = (wrap, payload) => {
    let line = wrap.querySelector('.paex-quick-add__rec-error');
    if (!line) {
      line = document.createElement('div');
      line.className = 'paex-quick-add__rec-error';
      line.setAttribute('role', 'status');
      wrap.append(line);
    }
    line.textContent =
      (payload && payload.errorLabel) ||
      t('addError', 'Could not add to cart. Please try again.', "Impossible d'ajouter au panier. Réessayez.");
  };

  const addRec = async (item, button, wrap, payload) => {
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      // M6 (2026-08-27): same bridge-first single-POST as the CTA add —
      // queued, token-gated paint, localized route; fallback hardened.
      const bridge = window.PaexCartBridge;
      const viaBridge = !!(bridge && typeof bridge.mutate === 'function');
      let cartData = null;
      if (viaBridge) {
        // BOPIS: a one-size add follows the cart's service, else the default (ship when the DC
        // has it, else pickup when gated) — paexFulfil.gate decides from handle + the payload's
        // fs entries; it only interrupts when the item is unavailable for that service.
        state.fulfil.intercepted = false;
        const recOpts = { source: 'paex-quick-add', fulfilSource: 'quick-add', handle: item.handle || '', url: item.url || '' };
        if (item.fs && item.variant_id) {
          recOpts.stock = {};
          recOpts.stock[String(item.variant_id)] = { id: String(item.variant_id), a: null, dc: item.fs.dc == null ? null : Number(item.fs.dc), stores: item.fs.stores || {}, su: null };
        }
        const resp = await bridge.mutate('cart/add.js', { id: item.variant_id, quantity: 1 }, recOpts);
        if (!resp && state.fulfil.intercepted) {
          state.fulfil.intercepted = false;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          return;
        }
        if (!resp || resp.status) throw new Error('add failed');
        cartData = resp;
      } else {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.variant_id, quantity: 1 }),
        });
        if (!response.ok) throw new Error('add failed');
        cartData = await response.json().catch(() => null);
      }
      button.removeAttribute('aria-busy');
      button.classList.add('is-added'); // flips to ✓ (design E-06: adds in place)
      button.textContent = '✓';
      button.setAttribute('aria-label', `${(payload && payload.addedLabel) || ''}: ${item.title || ''}`);
      const errorLine = wrap.querySelector('.paex-quick-add__rec-error');
      if (errorLine) errorLine.remove();
      if (!viaBridge) await refreshCartSections(cartData); // bridge path already painted + published
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      showRecError(wrap, payload);
    }
  };

  // Fix 21 (2026-08-02): decide the recs block's open-state footprint so
  // the desktop shell opens at ONE settled size (the audit's banned
  // two-stage motion was the panel growing 497→560px when the flyout
  // loaded). Cache KNOWN (majority — the pointerdown prefetch plus the
  // hover/focus warm below settle most payloads before the click):
  // items render immediately from cache, empty stays hidden — exact
  // either way. Unknown (first cold open): reserve the block's maximum
  // settled footprint (heading bar + 4 ghost cards = the design cap, two
  // desktop rows) and let the real payload replace it in place — a 3-4
  // item payload lands at the identical height; fewer items or an empty
  // answer collapse the difference in ONE step, usually inside the 260ms
  // flyout (the fetch has a head start), never as late post-open growth.
  // Desktop only: the mobile sheet is a fixed 88dvh, content settles
  // inside the scroll region (audit: 0px drift).
  const reserveRecs = (wrap) => {
    clear(wrap);
    delete wrap.dataset.renderedFor;
    const known = recsSettled.has(state.productPath);
    if (known) {
      const payload = recsSettled.get(state.productPath);
      const items = (payload && Array.isArray(payload.items) && payload.items) || [];
      if (items.length) renderRecs(payload); // settled size from frame one
      else wrap.hidden = true; // known no-recs: no reserve, no dead band
      return;
    }
    if (!window.matchMedia('(min-width: 768px)').matches) {
      wrap.hidden = true; // mobile: fixed sheet, keep the lazy fill
      return;
    }
    const heading = document.createElement('p');
    heading.className = 'paex-quick-add__recs-heading';
    const bar = document.createElement('span');
    bar.className = 'paex-quick-add__ph paex-quick-add__ph--recs';
    heading.append(bar);
    const list = document.createElement('div');
    list.className = 'paex-quick-add__recs-list';
    list.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 4; i++) {
      const ghost = document.createElement('span');
      ghost.className = 'paex-quick-add__rec paex-quick-add__rec--ph';
      const thumb = document.createElement('span');
      thumb.className = 'paex-quick-add__rec-thumb';
      ghost.append(thumb);
      list.append(ghost);
    }
    wrap.append(heading, list);
    wrap.hidden = false;
  };

  const renderRecs = (payload) => {
    const wrap = q('[data-paex-qa-recs]');
    if (!wrap) return;
    // Fix 21: reserveRecs may have already rendered this product's cached
    // payload at open — a second render would wipe rec-add ✓ states and
    // reflow images for nothing.
    if (wrap.dataset.renderedFor === state.productPath) return;
    clear(wrap);
    const items = (payload && Array.isArray(payload.items) && payload.items) || [];
    wrap.dataset.renderedFor = state.productPath;
    if (!items.length) {
      wrap.hidden = true; // block absent, no gap
      return;
    }
    const heading = document.createElement('p');
    heading.className = 'paex-quick-add__recs-heading';
    heading.textContent = payload.heading || '';
    const list = document.createElement('div');
    list.className = 'paex-quick-add__recs-list';
    recItems.clear();
    items.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'paex-quick-add__rec';
      card.dataset.recId = String(item.id || item.variant_id || '');
      recItems.set(card.dataset.recId, item);
      let thumb;
      if (item.image) {
        thumb = document.createElement('img');
        thumb.src = item.image;
        thumb.alt = '';
        thumb.loading = 'lazy';
      } else {
        thumb = document.createElement('span');
      }
      thumb.className = 'paex-quick-add__rec-thumb';
      const main = document.createElement('div');
      main.className = 'paex-quick-add__rec-main';
      // Title is a real button: the keyboard path for the design's
      // "tapping the row swaps the sheet to that product" (F-03/E-05).
      const title = document.createElement('button');
      title.type = 'button';
      title.className = 'paex-quick-add__rec-title';
      title.textContent = item.title || '';
      const price = document.createElement('div');
      price.className = 'paex-quick-add__rec-price';
      price.textContent = item.price || '';
      main.append(title, price);
      let action;
      if (item.single_tap && item.variant_id) {
        // One-size: the circle ADDS in place (silent) and flips to ✓.
        action = document.createElement('button');
        action.type = 'button';
        action.className = 'paex-quick-add__rec-action paex-quick-add__rec-action--add';
        // W3.8: the add circle draws the shared ATC glyph (bag+plus,
        // --paex-atc-icon-mask — same icon as the card circle) instead of
        // a bare '+'; the aria-label keeps the accessible name. The added
        // state below still swaps the content to ✓.
        const glyph = document.createElement('span');
        glyph.className = 'paex-quick-add__rec-glyph';
        glyph.setAttribute('aria-hidden', 'true');
        action.append(glyph);
        action.setAttribute('aria-label', `${payload.addLabel || ''}: ${item.title || ''}`);
        action.addEventListener('click', (event) => {
          event.stopPropagation(); // the card tap must not also swap
          addRec(item, action, wrap, payload);
        });
      } else {
        // Sized: the circle swaps the modal to that product, same as the
        // row (design: variant items swap the drawer — no PDP navigation;
        // "See full details" is the way out to the PDP).
        action = document.createElement('button');
        action.type = 'button';
        action.className = 'paex-quick-add__rec-action';
        action.textContent = '→';
        action.setAttribute('aria-label', item.title || '');
      }
      // Whole-card tap swaps in place (except the + add circle, which
      // stops propagation above).
      card.addEventListener('click', () => swapTo(item.url, item.title || ''));
      card.append(thumb, main, action);
      list.append(card);
    });
    wrap.append(heading, list);
    wrap.hidden = false;
    applyRecFilter(); // BOPIS: items out for the effective service hide; an empty slot collapses
  };

  /* ---------------- open flow + in-modal navigation ---------------- */

  const renderBack = () => {
    const back = q('[data-paex-qa-back]');
    if (!back) return;
    const top = state.stack[state.stack.length - 1];
    if (top) {
      back.hidden = false;
      q('[data-paex-qa-back-label]').textContent = top.title || '';
      back.setAttribute(
        'aria-label',
        t('backTo', 'Back to {title}', 'Retour à {title}').replace('{title}', top.title || '')
      );
    } else {
      back.hidden = true;
      q('[data-paex-qa-back-label]').textContent = '';
    }
  };

  // Shared loader for the initial open AND in-modal swaps (design E-05/
  // F-03: tapping a recommendation swaps the modal to that product in
  // place). On swap, focus moves to the (tabindex=-1) title and the
  // aria-live region announces the new product.
  const loadProduct = (productPath, cardTitle, isSwap) => {
    state.product = null;
    state.productPath = productPath;
    state.selections = [];
    renderLoading(cardTitle);
    renderBack();

    const token = ++state.requestToken;
    // Recommendations resolve in PARALLEL with the product (fix 21): when
    // reserveRecs held the unknown-case footprint, the earliest possible
    // answer replaces or collapses it — an already-settled promise lands
    // in a microtask, BEFORE the open frame ever paints. Memoized per
    // path, so a reopen renders from cache (and renderRecs no-ops when
    // reserveRecs already drew this product's cached payload).
    recsFor(productPath).then((payload) => {
      if (token !== state.requestToken) return;
      renderRecs(payload);
    });
    productFor(productPath)
      .then((product) => {
        if (token !== state.requestToken) return; // superseded by a newer open
        state.product = product;
        state.selections = (product.options || []).map(() => null);
        state.touched = false;
        state.sgPre = false;
        state.sg = null;
        // BOPIS: per-open context; the stock map is requested only when the rows will show
        state.fulfil.stock = null;
        state.fulfil.stockFor = '';
        state.fulfil.selected = null;
        state.fulfil.chosen = false;
        if (fulfilDecision()) fulfilLoadStock(product, token);
        sgAnnounced = false;
        sgAskEmitted = false;
        sgSwitchEmitted = new Set();
        // Phase 2: an island settled by warm() resolves + pre-selects SYNCHRONOUSLY, so
        // badge, checked pill and slot are in the FIRST render; otherwise the island's
        // then-callback does it once, only while the shopper has not touched a pill.
        if (sgSettled.has(productPath)) {
          sgPrepare(sgSettled.get(productPath));
          sgPreselect();
          renderGuidance();
        }
        renderProduct();
        sgAnnounce();
        // Size chart row (2026-10-02): memoized per path (warmed on
        // pointerdown with the product), gated on the kill switch, then the
        // product's tags or the recs payload's sg_mf flag (sgWhen).
        sgWhen(productPath, product).then((data) => {
          if (token !== state.requestToken) return;
          if (data && data.chart) ensureSg().catch(noop); // charted: the ONE stamp encoder lands before any add (§1.4: base once per PLP session)
          const fresh = !state.sg; // not prepared in the sync path above
          if (fresh) sgPrepare(data);
          const pending = !!(state.sg && !state.sg.evaluated);
          const finish = () => {
            if (token !== state.requestToken) return;
            // the logic asset may land AFTER the island: resolve once it is there
            if (state.sg && !state.sg.evaluated && sgLogicReady()) sgPrepare(state.sg.data);
            if (state.sg) { if (fresh || pending) { sgPreselect(); sgRerenderPills(); } }
            else renderGuidance(); // settled "no chart": release the slot reserve
            renderSizeRow(data);
          };
          // logic + row renderer only for profile holders on finder-on charts (§1.4 load rules)
          if (pending && sgHasProfiles() && (state.sg.data.settings || {}).finder !== false) ensureSgAll().then(finish, finish);
          else finish();
        });
        if (isSwap) {
          const live = q('[data-paex-qa-live]');
          if (live) live.textContent = product.title;
          const title = q('[data-paex-qa-title]');
          if (title && typeof title.focus === 'function') title.focus();
        }
      })
      .catch(() => {
        if (token !== state.requestToken) return;
        const rows = q('[data-paex-qa-rows]');
        clear(rows);
        rows.append(row('m', t('unavailable', 'Availability unavailable', 'Disponibilité non disponible')));
      });
  };

  const openFor = (trigger, productPath, cardTitle) => {
    const dialog = modal();
    state.opener = trigger;
    state.stack = []; // fresh open from a card = fresh navigation stack
    loadProduct(productPath, cardTitle, false);
    dialog.show(trigger);
  };

  // Swap the open modal to another product (recommendation tap). The stack
  // is capped at 5 — beyond that the top entry is replaced (design E-05).
  const swapTo = (path, title) => {
    if (!path) return;
    const current = { path: state.productPath, title: (state.product && state.product.title) || '' };
    if (state.stack.length >= 5) state.stack[state.stack.length - 1] = current;
    else state.stack.push(current);
    loadProduct(path, title || '', true);
  };

  const goBack = () => {
    const previous = state.stack.pop();
    if (!previous) return;
    loadProduct(previous.path, previous.title, true);
  };

  // Resolve the product from any element inside a card and open the modal.
  // Returns false when the shell or the product URL cannot be resolved (the
  // caller decides the fallback). Shared by the trigger interception below
  // and the card "+N" overflow chip (rcc-card-components.js) via the
  // window.paexQuickAdd hook.
  const openFromCard = (element) => {
    const dialog = modal();
    if (!dialog || typeof dialog.show !== 'function') return false;
    const card = element.closest && element.closest('.rcc-search__product');
    const anchor = card && card.querySelector('a[href*="/products/"]');
    if (!anchor) return false;
    // Custom bundles are never quick-addable (see the trigger guard below) —
    // refusing here also covers the size-overflow "+N" chip, whose caller
    // falls back to the card's product link on false.
    if (card.querySelector('rcc-product-inventory[data-bundle]')) return false;
    const path = anchor.pathname.replace(/\/+$/, '');
    const cardTitle = card.querySelector('.rcc-search__product__title');
    openFor(element, path, cardTitle ? cardTitle.textContent.trim() : '');
    return true;
  };

  window.paexQuickAdd = { open: openFromCard };

  // CAPTURE-phase interception on document: runs before the RCC app's own
  // handler regardless of where the app binds (document capture precedes
  // target/bubble listeners on descendants; among document capture
  // listeners ours registers first because this script is evaluated
  // before the app hydrates).
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest && event.target.closest('button.rcc-search__product__quick-add-trigger');
      if (!trigger) return;
      const card = trigger.closest('.rcc-search__product');
      const anchor = card && card.querySelector('a[href*="/products/"]');
      if (!anchor) return; // cannot resolve product → app modal fallback
      // Custom bundles (2026-08-12): this modal only knows the bundle
      // product's OWN variants, so adding from it would put an unconfigured
      // bundle in the cart. rcc-custom.css already hides the trigger on
      // [data-bundle] cards; this is the backstop for browsers without
      // :has() (and it must also swallow the event, or the app's own modal
      // takes over and offers the very add we are removing).
      if (card.querySelector('rcc-product-inventory[data-bundle]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        window.location.href = anchor.href;
        return;
      }
      const dialog = modal();
      if (!dialog || typeof dialog.show !== 'function') return; // shell missing → app modal fallback
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      openFromCard(trigger);
    },
    true
  );

  const wire = () => {
    const dialog = modal();
    if (!dialog) return;
    const close = dialog.querySelector('[data-paex-qa-close]');
    if (close) close.addEventListener('click', () => dialog.hide());
    const cta = dialog.querySelector('[data-paex-qa-cta]');
    if (cta) cta.addEventListener('click', onCtaClick); // W1 B4: same handler as swapped nodes
    const back = dialog.querySelector('[data-paex-qa-back]');
    if (back) back.addEventListener('click', goBack);
    // BOPIS: the service rows (native radios inside the modal's own fieldset)
    const services = dialog.querySelector('[data-paex-qa-services]');
    if (services) services.addEventListener('change', onServiceChange);
    // Phase 2: the ONE persistent guidance slot + the stacked drawer's close observer
    sgSlot = document.createElement('div');
    sgSlot.className = SG_CLASS_PREFIX + 'slot';
    sgSlot.setAttribute('data-paex-qa-sg-slot', '');
    sgSlot.hidden = true;
    const stacked = document.getElementById(SG_STACKED_ID);
    if (stacked && typeof MutationObserver === 'function') {
      new MutationObserver(() => { if (!stacked.hasAttribute('open')) sgAfterStackedClose(); }).observe(stacked, { attributes: true, attributeFilter: ['open'] });
    }
    // ESC BACKSTOP (2026-08-01): Ascent's ModalDialog only hears keyup
    // ESC when the event target is INSIDE the element — any focus escape
    // (Safari never focuses clicked buttons; Chromium loses focus when a
    // re-render removes the focused pill) made ESC dead. Document-level
    // listener scoped strictly to this dialog being open; hide() restores
    // focus to the opener via the existing openedBy contract. Phase 2: the stacked
    // drawer is the top layer — it closes first, the quick-add stays (§4.4.5).
    document.addEventListener('keyup', (event) => {
      if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
      if (stacked && stacked.hasAttribute('open') && typeof stacked.hide === 'function') { stacked.hide(); return; }
      if (dialog.hasAttribute('open')) dialog.hide();
    });
  };

  // Prefetch on pointerdown (fix round 7): the press starts the product +
  // recs requests ~100-300ms before the click lands, so the modal usually
  // populates within the flyout animation. Cheap no-op when already cached.
  const warm = (element) => {
    const card = element.closest('.rcc-search__product');
    const anchor = card && card.querySelector('a[href*="/products/"]');
    if (!anchor) return;
    const path = anchor.pathname.replace(/\/+$/, '');
    // Phase 2: profile holders also get the logic + row assets early (a badge is
    // possible before any drawer opens); everyone else pays nothing more than the island
    productFor(path)
      .then((product) => sgWhen(path, product))
      .then((data) => {
        if (data && data.chart && sgHasProfiles() && (data.settings || {}).finder !== false) ensureSgAll().catch(noop);
      })
      .catch(noop);
    recsFor(path);
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      const press =
        event.target.closest &&
        event.target.closest('button.rcc-search__product__quick-add-trigger, .paex-rcc-more-sizes');
      if (press) warm(press);
    },
    true
  );

  // Fix 21: hover/focus warm on pointer devices — the trigger only shows
  // on card hover, so by the time it is CLICKED the recs answer has had
  // 300ms+ to settle and reserveRecs can decide the desktop footprint
  // exactly (no blind reserve). Same memoized fetches, no-op when warm;
  // touch devices never fire mouseover before the tap and keep the
  // pointerdown head start above.
  document.addEventListener(
    'mouseover',
    (event) => {
      const hover =
        event.target.closest &&
        event.target.closest('button.rcc-search__product__quick-add-trigger, .paex-rcc-more-sizes');
      if (hover) warm(hover);
    },
    true
  );

  document.addEventListener(
    'focusin',
    (event) => {
      const focus =
        event.target.closest &&
        event.target.closest('button.rcc-search__product__quick-add-trigger, .paex-rcc-more-sizes');
      if (focus) warm(focus);
    },
    true
  );

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();
})();
